import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { 
  Plus, 
  Search, 
  Filter, 
  Settings,
  MoreHorizontal, 
  FileText, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Send,
  ArrowRight,
  Loader2,
  Printer,
  Mail,
  Edit,
  Trash2,
  Link as LinkIcon,
  RotateCcw,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  Download,
  Upload,
  Paperclip,
  X,
  QrCode,
  Tag as TagIcon,
  Bell,
  Calendar,
  Check,
  FileSpreadsheet,
  Sparkles,
  Copy,
  Truck
} from 'lucide-react';
import Papa from 'papaparse';
import { QRCodeSVG } from 'qrcode.react';
import { Logger } from '../services/historyService';
import { generateQuotePdf } from '../lib/quotePdf';
import { 
  db, 
  auth, 
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  increment
} from '../firebase';
import { cn, getPortalUrl } from '../lib/utils';
import { formatCurrency, normalizeCurrency } from '../lib/currency';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { CustomSelect } from './ui/CustomSelect';
import { SearchableSelect } from './ui/SearchableSelect';
import { DocumentPrintLayout } from './ui/DocumentPrintLayout';
import { notify } from '../services/notify';
import { CancelDocumentDialog, StatusBadge } from './workflow';
import DocumentChatterPanel, { type ChatterRelatedDocument } from './DocumentChatterPanel';
import {
  deriveDeliveryStatus,
  deriveInvoiceStatus,
  isDocumentExpired,
  normalizeDocumentStatus,
  type StatusBadgeValue,
} from '../lib/documentStatus';
import DiscussionsView from './DiscussionsView';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: any;
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  const isPermissionDenied = error instanceof Error && error.message.includes('Missing or insufficient permissions');
  const isLocalhost = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  if (isLocalhost && isPermissionDenied) {
    console.warn(`Ignoring Firestore permission error at ${path} during local development.`);
    return;
  }
  throw new Error(JSON.stringify(errInfo));
}

const hasLocalSession = () => typeof window !== 'undefined' && Boolean(localStorage.getItem('app_auth_token'));

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('app_auth_token');
  if (!token) throw new Error('Local session token is unavailable');

  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    let message = errorBody || `Request failed (${response.status})`;
    try {
      const parsed = JSON.parse(errorBody);
      message = parsed?.error || parsed?.message || message;
    } catch {
      // The API may return plain text instead of JSON.
    }
    throw new Error(message);
  }

  return response.status === 204 ? (undefined as T) : response.json();
};

const localApiRecords = <T,>(payload: any): T[] =>
  Array.isArray(payload) ? payload : payload?.documents || payload?.contacts || payload?.items ||
  payload?.accounts || payload?.chartOfAccounts || payload?.taxRates || payload?.tax_rates || payload?.data || [];

const localApiRecord = <T,>(payload: any): T =>
  (payload?.document || payload?.data || payload) as T;

const formatQuoteCurrency = (amount: number, currency: unknown) => formatCurrency(amount, currency);

const getQuoteRecipientEmails = (contact: any, fallbackEmail?: string) => {
  const contactCandidates = [
    contact?.email,
    ...(Array.isArray(contact?.emails) ? contact.emails : []),
  ];
  const contactEmails = contactCandidates.reduce<string[]>((emails, candidate) => {
    if (typeof candidate !== 'string') return emails;
    const email = candidate.trim();
    if (email && !emails.some(existing => existing.toLowerCase() === email.toLowerCase())) {
      emails.push(email);
    }
    return emails;
  }, []);

  if (contactEmails.length > 0) return contactEmails;
  return typeof fallbackEmail === 'string' && fallbackEmail.trim() ? [fallbackEmail.trim()] : [];
};

const getQuoteExpiryDate = (quote: { expiryDate?: string; dueDate?: string }) =>
  quote.expiryDate || quote.dueDate || '';

const createQuoteLineId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `quote-line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const normalizeQuoteLine = <T extends Record<string, any>>(line: T) => {
  const quoteLineId = String(line.quoteLineId || line.id || createQuoteLineId());
  return { ...line, id: String(line.id || quoteLineId), quoteLineId };
};

const normalizeQuoteRecord = <T extends { expiryDate?: string; dueDate?: string; items?: any[] }>(quote: T) => {
  const expiryDate = getQuoteExpiryDate(quote);
  return {
    ...quote,
    expiryDate,
    dueDate: expiryDate,
    items: Array.isArray(quote.items) ? quote.items.map(normalizeQuoteLine) : quote.items,
  };
};

const getQuoteStatusBadge = (quote: Pick<Quote, 'status' | 'expiryDate'> & { legacyStatus?: string }): StatusBadgeValue => {
  const rawStatus = String(quote.legacyStatus || quote.status || '').toLowerCase();
  if (rawStatus === 'sent') return isDocumentExpired(quote.expiryDate, 'draft') ? 'expired' : 'sent';
  if (rawStatus === 'accepted') return 'accepted';
  if (rawStatus === 'declined') return 'declined';
  if (rawStatus === 'deleted' || rawStatus === 'void') return 'cancelled';
  return normalizeDocumentStatus(quote.status);
};

type QuoteStage = 'Draft' | 'Sent' | 'Accepted' | 'Invoiced' | 'Declined' | 'Deleted';

// The workflow stage a quote is at, used to gate the header actions.
// `status` is persisted canonically (draft | done | cancelled), so the finer
// stage lives in `legacyStatus`. Older rows still hold the legacy value in
// `status` itself, so read whichever is present and never compare raw strings
// directly: 'Draft' and 'draft' must behave identically.
const getQuoteStage = (quote: Pick<Quote, 'status'> & { legacyStatus?: string }): QuoteStage => {
  const canonical = normalizeDocumentStatus(quote.status);
  const raw = String(quote.legacyStatus || quote.status || '').trim().toLowerCase();
  if (canonical === 'cancelled') return raw === 'deleted' || raw === 'void' ? 'Deleted' : 'Declined';
  if (canonical === 'done') return raw === 'invoiced' || raw === 'converted' ? 'Invoiced' : 'Accepted';
  return raw === 'sent' ? 'Sent' : 'Draft';
};

type QuoteCsvRow = Record<string, unknown>;

const normalizeQuoteCsvHeader = (header: string) =>
  header.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\*/g, '').replace(/[^a-z0-9]+/g, '');

const getQuoteCsvValue = (row: QuoteCsvRow, ...aliases: string[]) => {
  const normalizedAliases = new Set(aliases.map(normalizeQuoteCsvHeader));
  const entry = Object.entries(row).find(([header]) => normalizedAliases.has(normalizeQuoteCsvHeader(header)));
  return entry?.[1];
};

const getQuoteCsvText = (row: QuoteCsvRow, ...aliases: string[]) => {
  const value = getQuoteCsvValue(row, ...aliases);
  return value === null || value === undefined ? '' : String(value).trim();
};

const parseQuoteCsvNumber = (value: unknown) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : Number.NaN;
  const text = value === null || value === undefined ? '' : String(value).trim();
  if (!text) return Number.NaN;
  const normalized = text.startsWith('(') && text.endsWith(')')
    ? `-${text.slice(1, -1)}`
    : text;
  const parsed = Number(normalized.replace(/,/g, '').replace(/%$/, '').trim());
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const normalizeQuoteCsvDate = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value).trim();
  const match = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[T\s].*)?$/);
  if (!match) return '';

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const calculateQuoteExpiryDate = (startDate: string, expiryDays: number) => {
  if (!startDate) return '';
  const date = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + Math.max(0, expiryDays));
  return date.toISOString().split('T')[0];
};

type QuoteReminderTab = 'remind7DaysBefore' | 'remindOnDueDate' | 'remind7DaysAfter' | 'remind14DaysAfter';

type QuoteRemindersSettings = {
  enabled: boolean;
  remind7DaysBefore: boolean;
  remindOnDueDate: boolean;
  remind7DaysAfter: boolean;
  remind14DaysAfter: boolean;
  templates: Record<QuoteReminderTab, string>;
};

const normalizeQuoteReminderSettings = (
  value: unknown,
  fallback: QuoteRemindersSettings,
): QuoteRemindersSettings => {
  const settings = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
  const templates = settings.templates && typeof settings.templates === 'object' && !Array.isArray(settings.templates)
    ? settings.templates as Record<string, unknown>
    : {};
  const booleanValue = (key: 'enabled' | QuoteReminderTab) =>
    typeof settings[key] === 'boolean' ? settings[key] as boolean : fallback[key];
  const templateValue = (key: QuoteReminderTab) =>
    typeof templates[key] === 'string' ? templates[key] as string : fallback.templates[key];

  return {
    enabled: booleanValue('enabled'),
    remind7DaysBefore: booleanValue('remind7DaysBefore'),
    remindOnDueDate: booleanValue('remindOnDueDate'),
    remind7DaysAfter: booleanValue('remind7DaysAfter'),
    remind14DaysAfter: booleanValue('remind14DaysAfter'),
    templates: {
      remind7DaysBefore: templateValue('remind7DaysBefore'),
      remindOnDueDate: templateValue('remindOnDueDate'),
      remind7DaysAfter: templateValue('remind7DaysAfter'),
      remind14DaysAfter: templateValue('remind14DaysAfter'),
    },
  };
};

interface ActionLogEntry {
  action: string;
  user: string;
  email: string;
  timestamp: any;
  details?: string;
  linkId?: string;
  linkType?: 'invoice' | 'quote' | 'bill' | 'payment';
}

interface Quote {
  id: string;
  number: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  date: string;
  expiryDate: string;
  dueDate?: string;
  reference?: string;
  currency: string;
  brandingTheme: string;
  totalAmount: number;
  status: 'Draft' | 'Sent' | 'Approved' | 'Declined' | 'Accepted' | 'Invoiced' | 'Converted' | 'Deleted' | 'Void' | 'draft' | 'done' | 'cancelled';
  legacyStatus?: string;
  items: Array<Record<string, any> & { id: string; quoteLineId?: string }>;
  termsAndConditions?: string;
  billingAddress?: string;
  deliveryAddress?: string;
  trackingCategory?: string;
  tags?: string[];
  repeatingConfig?: {
    frequency: number;
    frequencyUnit: 'Week' | 'Month';
    startDate: string;
    endDate?: string;
    expiryDays: number;
    approveStatus: 'Draft' | 'Approved' | 'Sent';
    nextGenerationDate?: string;
    lastGeneratedDate?: string;
  };
  createdBy?: string;
  createdAt?: any;
  createdByName?: string;
  createdByEmail?: string;
  deletedBy?: string;
  deletedByName?: string;
  deletedByEmail?: string;
  deletedAt?: any;
  actionDetails?: string;
  declineReason?: string;
  actionLog?: ActionLogEntry[];
  invoiceId?: string;
  source?: string;
  purchaseOrderId?: string;
  notes?: string;
  type: 'quote' | 'repeating_quote';
  threads?: any[];
  attachments?: { name: string, url: string, type: string, size: number, createdAt: string }[];
}

export default function QuotesView({ orgId, onNavigate, initialQuoteId, onClearInitial, language = 'en' } : { language?: string,  orgId: string, onNavigate?: (tab: string, id?: string) => void, initialQuoteId?: string | null, onClearInitial?: () => void }) {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [allInvoices, setAllInvoices] = useState<any[]>([]);
  const [allDeliveries, setAllDeliveries] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [itemsList, setItemsList] = useState<any[]>([]);
  const [accountsList, setAccountsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    refNumber: true,
    discussion: true,
    to: true,
    issueDate: true,
    amountToInvoice: true,
    amountInvoiced: true,
    totalAmount: true,
    status: true,
    invoiceStatus: true,
    tag1: true,
    tag2: true,
    tag3: true,
    tag4: true,
    tag5: true,
  });
  const [columnOrder, setColumnOrder] = useState<string[]>([
    'refNumber', 'discussion', 'to', 'issueDate', 'amountToInvoice', 'amountInvoiced', 'totalAmount', 'tag1', 'tag2', 'tag3', 'tag4', 'tag5', 'status', 'invoiceStatus'
  ]);
  const [isRemindersModalOpen, setIsRemindersModalOpen] = useState(false);
  const [activeReminderTab, setActiveReminderTab] = useState<QuoteReminderTab>('remind7DaysBefore');
  const [isRepeatingQuote, setIsRepeatingQuote] = useState(false);
  const [remindersSettings, setRemindersSettings] = useState<QuoteRemindersSettings>({
    enabled: false,
    remind7DaysBefore: false,
    remindOnDueDate: false,
    remind7DaysAfter: false,
    remind14DaysAfter: false,
    templates: {
      remind7DaysBefore: `Dear [Customer Name],\n\nThis is a friendly reminder that your quote [Quote Number] is expiring soon on [Quote Expiry Date].\n\nQuote Details:\n[Quote Line Items]\n\nTotal Value: [Quotation Value]\n\nPlease let us know if you have any questions.\n\nBest regards,\nThe Team`,
      remindOnDueDate: `Dear [Customer Name],\n\nYour quote [Quote Number] expires today ([Quote Expiry Date]).\n\nQuote Details:\n[Quote Line Items]\n\nTotal Value: [Quotation Value]\n\nIf you would like to proceed, please accept the quote before the end of the day.\n\nBest regards,\nThe Team`,
      remind7DaysAfter: `Dear [Customer Name],\n\nWe noticed your quote [Quote Number] expired on [Quote Expiry Date].\n\nQuote Details:\n[Quote Line Items]\n\nTotal Value: [Quotation Value]\n\n[Invoice Status]\n\nIf you are still interested in these services, please contact us to renew the quote.\n\nBest regards,\nThe Team`,
      remind14DaysAfter: `Dear [Customer Name],\n\nFollowing up on quote [Quote Number] which expired two weeks ago. \n\nQuote Details:\n[Quote Line Items]\n\nTotal Value: [Quotation Value]\n\n[Invoice Status]\n\nWe would love to work with you. Please let us know if we can still help with your requirements.\n\nBest regards,\nThe Team`
    }
  });
  const [repeatingConfig, setRepeatingConfig] = useState<Quote['repeatingConfig']>({
    frequency: 1,
    frequencyUnit: 'Month',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    expiryDays: 7,
    approveStatus: 'Draft'
  });
  const [showColumnGear, setShowColumnGear] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<any[]>([]);
  const [organization, setOrganization] = useState<any>(null);

  useEffect(() => {
    if (!orgId) return;
    const fetchOrg = async () => {
      const docSnap = await getDoc(doc(db, 'organizations', orgId));
      if (docSnap.exists()) {
        setOrganization({ id: docSnap.id, ...docSnap.data() });
      }
    };
    fetchOrg();
  }, [orgId]);

  const handlePrint = (doc: any) => {
    setDocToPrint([doc]);
    setIsPrintModalOpen(true);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedQuotes, setSelectedQuotes] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showThread, setShowThread] = useState<string | null>(null);
  const [showHistoryNotes, setShowHistoryNotes] = useState(false);
  const [showGetLinkModal, setShowGetLinkModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showLinkedInvoices, setShowLinkedInvoices] = useState(false);
  const [showLinkedDeliveries, setShowLinkedDeliveries] = useState(false);
  const [showCopyToMenu, setShowCopyToMenu] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [quoteToDecline, setQuoteToDecline] = useState<Quote | null>(null);
  const [isDeclineSaving, setIsDeclineSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const appLanguage = language === 'ar' ? 'ar' : 'en';

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (hasLocalSession()) {
      notify.error('Attachments are unavailable in local mode.');
      return;
    }
    if (!e.target.files?.length || !selectedQuote) return;
    const file = e.target.files[0];
    
    // Safety check for file size (e.g., 30MB limit)
    if (file.size > 30 * 1024 * 1024) {
      notify.error("File is too large. Max size is 30MB");
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);
    
    const tryUpload = async () => {
      const storagePath = `attachments/${Date.now()}_${file.name}`;
      const fileRef = ref(storage, storagePath);
      
      try {
        const snapshot = await uploadBytes(fileRef, file);
        const downloadUrl = await getDownloadURL(snapshot.ref);
        
        const attachment = {
          name: file.name,
          url: downloadUrl,
          type: file.type,
          size: file.size,
          createdAt: new Date().toISOString()
        };
        
        await updateDoc(doc(db, 'documents', selectedQuote.id), {
          attachments: arrayUnion(attachment),
          actionLog: arrayUnion({
            action: 'File Attached',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || 'No Email',
            timestamp: new Date().toISOString(),
            details: `Attached file: ${file.name}`
          })
        });
        await Logger.log(orgId, 'ADD', 'Quotes', selectedQuote.id, selectedQuote.number, {
          details: `Attached file: ${file.name}`,
          fileName: file.name,
        });
        
        notify.confirm(`File "${file.name}" attached successfully`);
      } catch (error: any) {
        console.error("Upload error:", error);
        let errorMessage = `Failed to upload file (${error.code || 'Unknown error'})`;
        if (error.code === 'storage/unauthorized') errorMessage = "Permission denied. Check rules.";
        notify.error(errorMessage);
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        if (e.target) e.target.value = '';
      }
    };

    tryUpload();
  };

  const handleRemoveAttachment = async (attachmentUrl: string) => {
    if (hasLocalSession()) {
      notify.error('Attachments are unavailable in local mode.');
      return;
    }
    if (!selectedQuote) return;
    
    try {
      const updatedAttachments = selectedQuote.attachments?.filter(a => a.url !== attachmentUrl) || [];
      await updateDoc(doc(db, 'documents', selectedQuote.id), {
        attachments: updatedAttachments,
        actionLog: arrayUnion({
          action: 'File Removed',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || 'No Email',
          timestamp: new Date().toISOString(),
          details: `Removed an attachment`
        })
      });
      await Logger.log(orgId, 'REMOVE', 'Quotes', selectedQuote.id, selectedQuote.number, {
        details: 'Removed an attachment',
      });
      notify.confirm("Attachment removed");
    } catch (error) {
      console.error("Error removing attachment:", error);
      notify.error("Failed to remove attachment");
    }
  };

  const getQuoteContact = (quote: Quote) =>
    contacts.find(contact => contact.id === quote.contactId) ||
    contacts.find(contact => contact.name === quote.contactName);

  const buildQuoteEmailUrl = (quote: Quote) => {
    const contact = getQuoteContact(quote);
    const recipientEmails = getQuoteRecipientEmails(contact, quote.contactEmail);
    if (recipientEmails.length === 0) return null;

    const itemLines = (quote.items || []).map(item =>
      `• ${item.description || item.item || 'Item'} (${item.quantity || 0} x ${quote.currency} ${(item.unitPrice || 0).toLocaleString()}): ${quote.currency} ${(item.total || 0).toLocaleString()}`
    ).join('\n');
    const subtotal = (quote.items || []).reduce((sum, item) => sum + calculateItemSubtotal(item), 0);
    const tax = (quote.items || []).reduce((sum, item) => sum + calculateItemTax(item), 0);
    const subject = `Quote ${quote.number} from Accountex`;
    const body = `Dear ${contact?.name || quote.contactName || 'Customer'},

Please find the details for Quote ${quote.number}, prepared on ${new Date(quote.date).toLocaleDateString()}.

Line Items & Breakdown:
${itemLines || 'No line items'}

Financial Overview:
- Subtotal: ${quote.currency} ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Tax Amount: ${quote.currency} ${tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Quotation Value: ${quote.currency} ${(quote.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

This quote is valid until ${new Date(quote.expiryDate).toLocaleDateString()}.
${quote.termsAndConditions ? `\nAdditional Terms:\n${quote.termsAndConditions}\n` : ''}
Please let us know if you would like to proceed or require any adjustments.

Best regards,
Accountex Team`;

    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmails.join(','))}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleBulkAction = async (action: 'submit' | 'approve' | 'delete' | 'print' | 'email') => {
    if (hasLocalSession() && ['submit', 'approve'].includes(action)) {
      notify.error('Bulk quote updates are unavailable in local mode.');
      return;
    }
    if (selectedQuotes.length === 0) return;

    const selectedQuoteRecords = selectedQuotes
      .map(id => quotes.find(quote => quote.id === id))
      .filter((quote): quote is Quote => Boolean(quote));

    if (action === 'print') {
      setDocToPrint(selectedQuoteRecords);
      setIsPrintModalOpen(true);
      return;
    }

    if (action === 'email') {
      let openedCount = 0;
      let missingEmailCount = 0;
      let blockedCount = 0;

      selectedQuoteRecords.forEach(quote => {
        const gmailUrl = buildQuoteEmailUrl(quote);
        if (!gmailUrl) {
          missingEmailCount++;
          return;
        }

        const emailWindow = window.open(gmailUrl, '_blank');
        if (emailWindow) openedCount++;
        else blockedCount++;
      });

      if (openedCount > 0) notify.confirm(`Opened ${openedCount} quote email draft${openedCount === 1 ? '' : 's'}`);
      if (missingEmailCount > 0) notify.error(`${missingEmailCount} quote${missingEmailCount === 1 ? '' : 's'} skipped because the contact email is missing`);
      if (blockedCount > 0) notify.error(`${blockedCount} email draft${blockedCount === 1 ? ' was' : 's were'} blocked. Allow pop-ups and try again.`);
      return;
    }

    try {
      if (action === 'delete') {
        if (hasLocalSession()) {
          await Promise.all(selectedQuotes.map(id =>
            localApiRequest(`/api/organizations/${orgId}/documents/${id}`, {
              method: 'PATCH',
              body: JSON.stringify({ status: 'Deleted', deletedAt: new Date().toISOString() }),
            })
          ));
          setQuotes(current => current.map(quote =>
            selectedQuotes.includes(quote.id) ? { ...quote, status: 'Deleted' } : quote
          ));
        } else {
          for (const id of selectedQuotes) {
            await updateDoc(doc(db, 'documents', id), { status: 'Deleted', deletedAt: serverTimestamp() });
            const quote = quotes.find(record => record.id === id);
            if (quote) {
              await Logger.log(orgId, 'STATUS_CHANGE', 'Quotes', id, quote.number, {
                from: quote.status,
                to: 'Deleted',
                details: `Quote ${quote.number} deleted`,
              });
            }
          }
        }
        notify.confirm(`${selectedQuotes.length} quote${selectedQuotes.length === 1 ? '' : 's'} deleted`);
      } else if (action === 'approve') {
        for (const id of selectedQuotes) {
          await updateDoc(doc(db, 'documents', id), { status: 'Accepted' });
          const quote = quotes.find(record => record.id === id);
          if (quote) {
            await Logger.log(orgId, 'STATUS_CHANGE', 'Quotes', id, quote.number, {
              from: quote.status,
              to: 'Accepted',
              details: `Quote ${quote.number} approved`,
            });
          }
        }
        notify.confirm(`${selectedQuotes.length} quotes approved`);
      } else if (action === 'submit') {
        for (const id of selectedQuotes) {
          await updateDoc(doc(db, 'documents', id), { status: 'Sent' });
          const quote = quotes.find(record => record.id === id);
          if (quote) {
            await Logger.log(orgId, 'STATUS_CHANGE', 'Quotes', id, quote.number, {
              from: quote.status,
              to: 'Sent',
              details: `Quote ${quote.number} submitted`,
            });
          }
        }
        notify.confirm(`${selectedQuotes.length} quotes submitted for approval`);
      }

      setSelectedQuotes([]);
    } catch (error) {
      console.error("Error performing bulk action:", error);
      notify.error("Failed to perform bulk action");
    }
  };
  const handleSaveReminders = async () => {
    try {
      const settingsToSave = normalizeQuoteReminderSettings(remindersSettings, remindersSettings);
      if (hasLocalSession()) {
        await localApiRequest(`/api/settings/${orgId}`, {
          method: 'PATCH',
          body: JSON.stringify({ remindersSettings: settingsToSave }),
        });
      } else {
        await updateDoc(doc(db, 'settings', orgId), {
          remindersSettings: settingsToSave
        });
      }
      Logger.log(orgId, 'SETTINGS_CHANGE', 'Quotes', orgId, 'Organization Settings', {
        action: 'Updated Quote Reminders',
        settings: settingsToSave
      });
      notify.confirm("Reminder settings saved successfully");
      setIsRemindersModalOpen(false);
    } catch (error) {
      console.error("Error saving reminders:", error);
      notify.error("Failed to save reminder settings");
    }
  };

  const [quoteToDelete, setQuoteToDelete] = useState<string | null>(null);
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Advanced filters
  const [refFilter, setRefFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [minTotalAmount, setMinTotalAmount] = useState('');
  const [maxTotalAmount, setMaxTotalAmount] = useState('');
  const [minInvoicedAmount, setMinInvoicedAmount] = useState('');
  const [maxInvoicedAmount, setMaxInvoicedAmount] = useState('');
  const [minToInvoiceAmount, setMinToInvoiceAmount] = useState('');
  const [maxToInvoiceAmount, setMaxToInvoiceAmount] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);
  
  // New Contact Modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '', type: 'customer' });

  // New Account Modal state
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ code: '', name: '', type: 'Revenue', description: '', taxRate: '0%' });
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [activeAccountDropdown, setActiveAccountDropdown] = useState<number | null>(null);
  const announceDropdownOpen = useExclusiveDropdown('quotes', () => {
    setShowExportDropdown(false);
    setShowImportDropdown(false);
    setActiveAccountDropdown(null);
  });

  // Click outside listener for account dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeAccountDropdown !== null) {
        const target = event.target as HTMLElement;
        if (!target.closest('.relative')) {
          setActiveAccountDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeAccountDropdown]);

  // Tax calculation helpers
  const parseTaxRate = (rateStr: string) => {
    if (!rateStr) return 0;
    // Check if it's a dynamic tax rate name
    const dynamicTax = taxRates.find(t => t.name === rateStr);
    if (dynamicTax) return (dynamicTax.rate || 0) / 100;
    
    const match = rateStr.match(/(\d+)%/);
    return match ? parseFloat(match[1]) / 100 : 0;
  };

  const calculateItemSubtotal = (item: any) => {
    const subtotalBeforeDiscount = item.quantity * item.unitPrice;
    const discountAmount = item.discountType === 'amount' 
      ? (item.discount || 0) 
      : (subtotalBeforeDiscount * (item.discount || 0) / 100);
    return subtotalBeforeDiscount - discountAmount;
  };

  const calculateItemTax = (item: any) => {
    const subtotal = calculateItemSubtotal(item);
    return subtotal * parseTaxRate(item.taxRate);
  };

  const calculateItemTotal = (item: any) => {
    return calculateItemSubtotal(item) + calculateItemTax(item);
  };
  
  // Currencies state
  const [currencies, setCurrencies] = useState(['USD', 'EUR', 'GBP', 'AED']);
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false);
  const [customCurrency, setCustomCurrency] = useState('');
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [nextQuoteNumber, setNextQuoteNumber] = useState<number>(1);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [brandingThemes, setBrandingThemes] = useState<any[]>([]);
  const [taxRates, setTaxRates] = useState<any[]>([]);

  // Partial Invoicing state
  const [isPartialInvoiceModalOpen, setIsPartialInvoiceModalOpen] = useState(false);
  const [partialInvoiceItems, setPartialInvoiceItems] = useState<any[]>([]);
  const [isPartialSaving, setIsPartialSaving] = useState(false);

  // Form state
  const [newQuote, setNewQuote] = useState<Omit<Quote, 'id'> & { trackingCategory?: string; contactName?: string; contactEmail?: string; tags?: string[] }>({
    number: '',
    contactId: '',
    contactName: '',
    contactEmail: '',
    date: new Date().toISOString().split('T')[0],
    expiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    reference: '',
    currency: normalizeCurrency(undefined),
    brandingTheme: 'Standard',
    items: [{ 
      id: createQuoteLineId(),
      quoteLineId: createQuoteLineId(),
      item: '', 
      description: '', 
      quantity: 1, 
      unitPrice: 0,
      discount: 0,
      discountType: 'percentage',
      total: 0,
      account: '',
      taxRate: '0%',
      trackingCategory: ''
    }],
    totalAmount: 0,
    status: 'Draft',
    termsAndConditions: '',
    billingAddress: '',
    deliveryAddress: '',
    trackingCategory: '',
    type: 'quote',
    notes: '',
    tags: []
  });

  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-quote') {
        setIsEditing(false);
        setIsRepeatingQuote(false);
        setNewQuote({
          number: '',
          contactId: '',
          contactName: '',
          contactEmail: '',
          date: new Date().toISOString().split('T')[0],
          expiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          reference: '',
          currency: normalizeCurrency(orgSettings?.defaultCurrency),
          brandingTheme: 'Standard',
          items: [],
          totalAmount: 0,
          status: 'Draft',
          termsAndConditions: '',
          billingAddress: '',
          deliveryAddress: '',
          trackingCategory: '',
          type: 'quote',
          notes: '',
          tags: []
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, [orgSettings]);

  useEffect(() => {
    if (!orgId) return;

    if (hasLocalSession()) {
      let cancelled = false;
      const loadLocalQuoteData = async () => {
        setLoading(true);
        try {
          await localApiRequest(`/api/organizations/${orgId}/repeating-quotes/generate`, {
            method: 'POST',
            body: JSON.stringify({ throughDate: new Date().toISOString().split('T')[0] }),
          });
          const [documentsResponse, invoicesResponse, deliveriesResponse, contactsResponse, itemsResponse, accountsResponse, taxRatesResponse, settingsResponse] = await Promise.all([
            localApiRequest(`/api/organizations/${orgId}/documents?types=quote,repeating_quote`),
            localApiRequest(`/api/organizations/${orgId}/documents?types=invoice`),
            localApiRequest(`/api/organizations/${orgId}/documents?types=delivery`),
            localApiRequest(`/api/organizations/${orgId}/contacts`),
            localApiRequest(`/api/organizations/${orgId}/items`),
            localApiRequest(`/api/organizations/${orgId}/chart-of-accounts`),
            localApiRequest(`/api/organizations/${orgId}/tax-rates`),
            localApiRequest(`/api/organizations/${orgId}/settings`),
          ]);
          if (cancelled) return;

          const settingsData = settingsResponse as any;
          if (settingsData) {
            const defaultCurrency = normalizeCurrency(
              settingsData.defaultCurrency ?? settingsData.default_currency ?? settingsData.currency
            );
            const normalizedSettings = {
              ...settingsData,
              defaultCurrency,
              allowedCurrencies: settingsData.allowedCurrencies ?? settingsData.allowed_currencies ?? [],
              defaultPaymentTerms: settingsData.defaultPaymentTerms ?? settingsData.default_payment_terms,
              quotePrefix: settingsData.quotePrefix ?? settingsData.quote_prefix,
              nextQuoteNumber: settingsData.nextQuoteNumber ?? settingsData.next_quote_number,
            };
            setOrgSettings(normalizedSettings);
            setNewQuote(prev => ({ ...prev, currency: defaultCurrency }));
            setCurrencies([...new Set([defaultCurrency, ...normalizedSettings.allowedCurrencies.map((value: unknown) => normalizeCurrency(value)).filter(Boolean)])]);

            const savedReminders = settingsData.data?.remindersSettings ?? settingsData.remindersSettings;
            if (savedReminders && typeof savedReminders === 'object' && !Array.isArray(savedReminders)) {
              setRemindersSettings(prev => normalizeQuoteReminderSettings(savedReminders, prev));
            }
          }

          setQuotes([...localApiRecords<Quote>(documentsResponse).map(normalizeQuoteRecord)].sort((a, b) => b.date.localeCompare(a.date)));
          setAllInvoices(localApiRecords<any>(invoicesResponse));
          setAllDeliveries(localApiRecords<any>(deliveriesResponse));
          setContacts(localApiRecords<any>(contactsResponse).filter(contact => {
            const type = String(contact.type || '').toLowerCase();
            return !type || type === 'customer' || type === 'both';
          }));
          setItemsList(localApiRecords<any>(itemsResponse));
          setAccountsList(localApiRecords<any>(accountsResponse));
          setTaxRates(localApiRecords<any>(taxRatesResponse));
        } catch (error) {
          console.error('Local quote data load failed:', error);
          notify.error('Failed to load quotes');
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      loadLocalQuoteData();
      return () => { cancelled = true; };
    }

    // Fetch organization settings
    const fetchOrgSettings = async () => {
      try {
        const docRef = doc(db, 'settings', orgId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          const defaultCurrency = normalizeCurrency(data.defaultCurrency ?? data.default_currency ?? data.currency, 'EGP');
          const normalizedSettings = {
            ...data,
            defaultCurrency,
            allowedCurrencies: data.allowedCurrencies ?? data.allowed_currencies ?? [],
            defaultPaymentTerms: data.defaultPaymentTerms ?? data.default_payment_terms,
            quotePrefix: data.quotePrefix ?? data.quote_prefix,
            nextQuoteNumber: data.nextQuoteNumber ?? data.next_quote_number,
          };
          setOrgSettings(normalizedSettings);
          
          // Update initial form state with settings
          const prefix = normalizedSettings.quotePrefix || 'QT-';
          let nextNum = normalizedSettings.nextQuoteNumber || 1;

          setNextQuoteNumber(nextNum);
          setNewQuote(prev => ({
            ...prev,
            number: `${prefix}${nextNum}`,
            currency: defaultCurrency,
            brandingTheme: normalizedSettings.layouts?.quotationThemeId || normalizedSettings.defaultInvoiceBrandingTheme || 'Standard',
            expiryDate: new Date(Date.now() + (normalizedSettings.defaultPaymentTerms || 15) * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }));
          
          if (normalizedSettings.allowedCurrencies.length > 0) {
            const allCurrencies = [...new Set([defaultCurrency, ...normalizedSettings.allowedCurrencies.map((value: unknown) => normalizeCurrency(value)).filter(Boolean)])];
            setCurrencies(allCurrencies);
          } else {
            setCurrencies([defaultCurrency]);
          }

          if (data.remindersSettings) {
            setRemindersSettings(prev => normalizeQuoteReminderSettings(data.remindersSettings, prev));
          }
        } else {
          setNewQuote(prev => ({
            ...prev,
            number: `QT-${Math.floor(1000 + Math.random() * 9000)}`
          }));
        }
      } catch (error) {
        console.error('Error fetching org settings:', error);
      }
    };

    fetchOrgSettings();

    if (!orgId) return;

    const q = query(
      collection(db, 'documents'), 
      where('type', 'in', ['quote', 'repeating_quote']),
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(doc => normalizeQuoteRecord({ id: doc.id, ...doc.data() } as Quote));
      setQuotes(docs);
      setLoading(false);
    });

    // Fetch all invoices for the organization to calculate invoiced amounts
    const invoicesQ = query(
      collection(db, 'documents'),
      where('type', '==', 'invoice'),
      where('orgId', '==', orgId)
    );
    const unsubscribeInvoices = onSnapshot(invoicesQ, (snapshot) => {
      setAllInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const deliveriesQ = query(
      collection(db, 'delivery_notes'),
      where('orgId', '==', orgId)
    );
    const unsubscribeDeliveries = onSnapshot(deliveriesQ, (snapshot) => {
      setAllDeliveries(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, error => console.warn('Could not load linked deliveries:', error));

    // Fetch Tracking Categories
    const trackingQ = query(
      collection(db, 'tracking_categories'), 
      where('orgId', '==', orgId),
      where('status', '==', 'active')
    );
    const unsubscribeTracking = onSnapshot(trackingQ, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Error fetching tracking categories:", error);
    });

    // Fetch contacts for the dropdown
    const contactsQ = query(
      collection(db, 'contacts'), 
      where('orgId', '==', orgId),
      where('type', 'in', ['Customer', 'Both'])
    );
    const unsubscribeContacts = onSnapshot(contactsQ, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch items for the dropdown
    const itemsQ = query(
      collection(db, 'items'), 
      where('orgId', '==', orgId),
      orderBy('name', 'asc')
    );
    const unsubscribeItems = onSnapshot(itemsQ, (snapshot) => {
      setItemsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch accounts for the dropdown
    const accountsQ = query(
      collection(db, 'chart_of_accounts'), 
      where('orgId', '==', orgId),
      orderBy('code', 'asc')
    );
    const unsubscribeAccounts = onSnapshot(accountsQ, (snapshot) => {
      setAccountsList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Fetch Branding Themes
    const themesQ = query(
      collection(db, 'branding_themes'),
      where('orgId', '==', orgId)
    );
    const unsubscribeThemes = onSnapshot(themesQ, (snapshot) => {
      setBrandingThemes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'branding_themes');
    });

    // Fetch Tax Rates
    const taxRatesQ = query(
      collection(db, 'tax_rates'),
      where('orgId', '==', orgId)
    );
    const unsubscribeTaxRates = onSnapshot(taxRatesQ, (snapshot) => {
      setTaxRates(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tax_rates');
    });

    // Fetch Tags
    const tagsQ = query(
      collection(db, 'tags'),
      where('orgId', '==', orgId),
      where('type', 'in', ['sales', null]),
      orderBy('createdAt', 'desc')
    );
    const unsubscribeTags = onSnapshot(tagsQ, (snapshot) => {
      setAvailableTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error("Error fetching tags:", error);
    });

    return () => {
      unsubscribe();
      unsubscribeInvoices();
      unsubscribeDeliveries();
      unsubscribeTracking();
      unsubscribeContacts();
      unsubscribeItems();
      unsubscribeAccounts();
      unsubscribeThemes();
      unsubscribeTaxRates();
      unsubscribeTags();
    };
  }, [orgId]);

  useEffect(() => {
    if (initialQuoteId) {
      if (initialQuoteId.startsWith('new-quote-for:')) {
        const contactId = initialQuoteId.split(':')[1];
        setIsEditing(false);
        setIsRepeatingQuote(false);
        setNewQuote(prev => {
          if (prev.contactId === contactId && prev.status === 'Draft') return prev;
          return {
            number: '',
            contactId: contactId,
            contactName: '',
            contactEmail: '',
            date: new Date().toISOString().split('T')[0],
            expiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            reference: '',
            currency: normalizeCurrency(orgSettings?.defaultCurrency),
            brandingTheme: 'Standard',
            items: [],
            totalAmount: 0,
            status: 'Draft',
            termsAndConditions: '',
            billingAddress: '',
            deliveryAddress: '',
            trackingCategory: '',
            type: 'quote',
            notes: '',
            tags: []
          };
        });
        setIsModalOpen(true);
        onClearInitial?.();
      } else if (quotes.length > 0) {
        const quote = quotes.find(q => q.id === initialQuoteId);
        if (quote && selectedQuote?.id !== quote.id) {
          setSelectedQuote(quote);
          onClearInitial?.();
        }
      }
    }
  }, [initialQuoteId, quotes.length > 0]);

  useEffect(() => {
    if (selectedQuote && quotes.length > 0) {
      const updatedQuote = quotes.find(q => q.id === selectedQuote.id);
      if (updatedQuote && JSON.stringify(updatedQuote) !== JSON.stringify(selectedQuote)) {
        setSelectedQuote(updatedQuote);
      }
    }
  }, [quotes]);

  const handleContactChange = (contactId: string) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      const email = contact.email || (contact.emails && contact.emails.length > 0 ? contact.emails[0] : '');
      setNewQuote(prev => ({
        ...prev,
        contactId,
        contactName: contact.name || '',
        contactEmail: email,
        currency: contact.salesCurrency || contact.currency || prev.currency,
        brandingTheme: contact.salesBrandingTheme || prev.brandingTheme,
        billingAddress: contact.billingAddress || contact.address || '',
        deliveryAddress: contact.deliveryAddress || contact.address || '',
        termsAndConditions: contact.salesPaymentTerms ? `Payment Terms: ${contact.salesPaymentTerms}` : (contact.paymentTerms ? `Payment Terms: ${contact.paymentTerms}` : prev.termsAndConditions),
        items: (prev.items || []).map((item, index) => index === 0 ? {
          ...item,
          account: contact.salesAccount || item.account,
          taxRate: contact.salesTaxRate || item.taxRate
        } : item)
      }));
    } else {
      setNewQuote(prev => ({ ...prev, contactId }));
    }
  };

  const handleExportExcel = () => {
    const data = filteredQuotes.map(quote => {
      const contact = contacts.find(c => c.id === quote.contactId);
      
      // Calculate billing metrics for export
      const invoicesForQuote = getLinkedInvoicesForQuote(quote);
      const amountInvoiced = invoicesForQuote.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
      const totalQuoteAmount = quote.totalAmount || 0;
      const amountToInvoice = Math.max(0, totalQuoteAmount - amountInvoiced);

      return {
        'Ref Number': quote.number,
        'Customer': contact?.name || quote.contactName || 'Walk-in Customer',
        'Date': quote.date,
        'Amount to Invoice': amountToInvoice,
        'Amount Invoiced': amountInvoiced,
        'Quotation Value': totalQuoteAmount,
        'Status': quote.status,
        'Tag 1': quote.tags?.[0] || '',
        'Tag 2': quote.tags?.[1] || '',
        'Tag 3': quote.tags?.[2] || '',
        'Tag 4': quote.tags?.[3] || '',
        'Tag 5': quote.tags?.[4] || '',
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotes");
    XLSX.writeFile(workbook, `Quotes_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
    notify.confirm("Excel file exported successfully");
  };
  
  const handleDownloadCSVTemplate = () => {
    const headers = [
      'Contact*',
      'Quote Number',
      'Date*',
      'Expiry Date*',
      'Reference',
      'Currency',
      'Description*',
      'Quantity*',
      'Price*',
      'Discount',
      'Account',
      'Tax Rate'
    ];

    trackingCategories.forEach((_, index) => headers.push(`tracking option ${index + 1}`));
    availableTags.forEach((_, index) => headers.push(`TAG ${index + 1}`));

    const prefix = String(orgSettings?.quotePrefix ?? orgSettings?.quote_prefix ?? 'QT-').trim() || 'QT-';
    const configuredNextNumber = Number(orgSettings?.nextQuoteNumber ?? orgSettings?.next_quote_number ?? nextQuoteNumber);
    const exampleNumber = `${prefix}${Number.isFinite(configuredNextNumber) && configuredNextNumber > 0 ? Math.floor(configuredNextNumber) : 1}`;
    const today = new Date().toISOString().split('T')[0];
    const expiryDate = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const exampleRow = [
      'ABC Company', exampleNumber, today, expiryDate, 'REF-001', normalizeCurrency(orgSettings?.defaultCurrency ?? orgSettings?.default_currency),
      'Weekly Maintenance', 1, 500, 0, '200', '15%',
      ...trackingCategories.map((_, index) => `Region ${index + 1}`),
      ...availableTags.map((_, index) => `Tag ${index + 1} Value`),
    ];
    const csvContent = Papa.unparse({ fields: headers, data: [exampleRow] });
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Quotations_Template.csv';
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    notify.confirm('CSV template downloaded successfully');
  };

  const handleImportCSV = () => {
    const isLocal = hasLocalSession();
    if (!isLocal && !auth.currentUser) {
      notify.error('You must be signed in to import quotes.');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,text/csv';
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      Papa.parse<QuoteCsvRow>(file, {
        header: true,
        skipEmptyLines: 'greedy',
        transformHeader: header => header.replace(/^\uFEFF/, '').trim(),
        complete: async results => {
          const toastId = notify.loading(`Importing quotes from ${file.name}...`);
          try {
            if (results.errors.length > 0) {
              throw new Error(`CSV parsing failed: ${results.errors[0].message}`);
            }

            const data = results.data.filter(row => Object.values(row).some(value => String(value ?? '').trim()));
            if (data.length === 0) throw new Error('The CSV file does not contain any quote rows.');

            const headers = new Set((results.meta.fields || []).map(normalizeQuoteCsvHeader));
            const requiredHeaders: Array<[string, string[]]> = [
              ['Contact', ['contact', 'contactname', 'thecontact']],
              ['Date', ['date', 'quotedate', 'thedate']],
              ['Expiry Date', ['expirydate', 'theexpirydate']],
              ['Description', ['description']],
              ['Quantity', ['quantity']],
              ['Price', ['price', 'unitamount', 'unitprice']],
            ];
            const missingHeaders = requiredHeaders
              .filter(([, aliases]) => !aliases.some(alias => headers.has(alias)))
              .map(([label]) => label);
            if (missingHeaders.length > 0) {
              throw new Error(`Missing required CSV columns: ${missingHeaders.join(', ')}. Download the Quotes CSV template and try again.`);
            }

            const grouped = new Map<string, { rows: QuoteCsvRow[]; requestedNumber: string }>();
            data.forEach((row, index) => {
              const requestedNumber = getQuoteCsvText(row, 'Quote Number', 'QuoteNumber');
              const reference = getQuoteCsvText(row, 'Reference', 'The reference');
              const key = requestedNumber
                ? `number:${requestedNumber.toLowerCase()}`
                : reference
                  ? `reference:${reference.toLowerCase()}`
                  : `row:${index}`;
              const group = grouped.get(key);
              if (group) group.rows.push(row);
              else grouped.set(key, { rows: [row], requestedNumber });
            });

            const prefix = String(orgSettings?.quotePrefix ?? orgSettings?.quote_prefix ?? 'QT-').trim() || 'QT-';
            const configuredNextNumber = Number(orgSettings?.nextQuoteNumber ?? orgSettings?.next_quote_number ?? nextQuoteNumber);
            let nextNumberCandidate = Number.isFinite(configuredNextNumber) && configuredNextNumber > 0
              ? Math.floor(configuredNextNumber)
              : 1;
            const usedNumbers = new Set(
              quotes.filter(quote => quote.type === 'quote').map(quote => quote.number.trim().toLowerCase())
            );
            const allocateQuoteNumber = () => {
              let number = `${prefix}${nextNumberCandidate}`;
              while (usedNumbers.has(number.toLowerCase())) {
                nextNumberCandidate += 1;
                number = `${prefix}${nextNumberCandidate}`;
              }
              usedNumbers.add(number.toLowerCase());
              nextNumberCandidate += 1;
              return number;
            };

            const defaultCurrency = normalizeCurrency(orgSettings?.defaultCurrency ?? orgSettings?.default_currency);
            const importedByName = auth.currentUser?.displayName || 'CSV Import';
            const importedByEmail = auth.currentUser?.email || 'Local session';
            const newQuotes: Array<Record<string, unknown>> = [];

            for (const { rows, requestedNumber } of grouped.values()) {
              const firstRow = rows[0];
              const contactName = getQuoteCsvText(firstRow, 'Contact', 'ContactName', 'The contact');
              if (!contactName) throw new Error('Every quote must include a Contact value.');

              const dateText = getQuoteCsvText(firstRow, 'Date', 'Quote Date', 'The date');
              const date = normalizeQuoteCsvDate(dateText);
              if (!date) throw new Error(`Quote for ${contactName} has an invalid Date. Use YYYY-MM-DD.`);

              const expiryDateText = getQuoteCsvText(firstRow, 'Expiry Date', 'ExpiryDate', 'The expiry date');
              const expiryDate = normalizeQuoteCsvDate(expiryDateText);
              if (!expiryDate) throw new Error(`Quote for ${contactName} has an invalid Expiry Date. Use YYYY-MM-DD.`);

              const number = requestedNumber || allocateQuoteNumber();
              if (requestedNumber) {
                const normalizedNumber = requestedNumber.toLowerCase();
                if (usedNumbers.has(normalizedNumber)) throw new Error(`Quote number ${requestedNumber} already exists.`);
                usedNumbers.add(normalizedNumber);
              }

              const reference = getQuoteCsvText(firstRow, 'Reference', 'The reference');
              const rawCurrency = getQuoteCsvText(firstRow, 'Currency', 'The currency');
              const currency = normalizeCurrency(rawCurrency, defaultCurrency);
              const extractedTags = Object.entries(firstRow)
                .filter(([header, value]) => normalizeQuoteCsvHeader(header).startsWith('tag') && String(value ?? '').trim())
                .map(([, value]) => String(value).trim())
                .filter((tag, index, tags) => tags.indexOf(tag) === index);

              const items = rows.map((row, rowIndex) => {
                const description = getQuoteCsvText(row, 'Description');
                if (!description) throw new Error(`Row ${rowIndex + 1} for quote ${number} is missing Description.`);

                const quantity = parseQuoteCsvNumber(getQuoteCsvValue(row, 'Quantity'));
                if (!Number.isFinite(quantity) || quantity <= 0) {
                  throw new Error(`Row ${rowIndex + 1} for quote ${number} has an invalid Quantity.`);
                }

                const unitPrice = parseQuoteCsvNumber(getQuoteCsvValue(row, 'Price', 'Unit Amount', 'UnitAmount', 'Unit Price'));
                if (!Number.isFinite(unitPrice)) {
                  throw new Error(`Row ${rowIndex + 1} for quote ${number} has an invalid Price.`);
                }

                const rawDiscount = getQuoteCsvValue(row, 'Discount');
                const discount = String(rawDiscount ?? '').trim() ? parseQuoteCsvNumber(rawDiscount) : 0;
                if (!Number.isFinite(discount) || discount < 0) {
                  throw new Error(`Row ${rowIndex + 1} for quote ${number} has an invalid Discount.`);
                }

                const trackingParts = trackingCategories.flatMap((category, index) => {
                  const value = getQuoteCsvText(row, `tracking option ${index + 1}`);
                  return value ? [`${category.id}:${value}`] : [];
                });
                const item = {
                  id: createQuoteLineId(),
                  quoteLineId: '',
                  item: '',
                  description,
                  quantity,
                  unitPrice,
                  discount,
                  discountType: 'percentage',
                  account: getQuoteCsvText(row, 'Account', 'Account Code', 'AccountCode'),
                  taxRate: getQuoteCsvText(row, 'Tax Rate', 'TaxRate') || '0%',
                  trackingCategory: trackingParts.join('|'),
                  total: 0,
                };
                item.quoteLineId = item.id;
                item.total = calculateItemTotal(item);
                return item;
              });

              const totalAmount = items.reduce((sum, item) => sum + item.total, 0);
              const contact = contacts.find(contactRecord =>
                String(contactRecord.name || '').trim().toLowerCase() === contactName.toLowerCase()
              );
              const actionDetails = `Imported Quote ${number} for ${contactName} from CSV`;
              const actionLog: ActionLogEntry[] = [{
                action: 'Imported',
                user: importedByName,
                email: importedByEmail,
                timestamp: new Date().toISOString(),
                details: actionDetails,
              }];

              newQuotes.push({
                number,
                reference,
                contactId: contact?.id || '',
                contactName,
                contactEmail: contact?.email || contact?.emails?.[0] || '',
                date,
                expiryDate,
                dueDate: expiryDate,
                items,
                totalAmount,
                tags: extractedTags,
                status: 'Draft',
                type: 'quote',
                orgId,
                currency,
                brandingTheme: 'Standard',
                amountsAre: 'Tax Exclusive',
                actionDetails,
                actionLog,
                createdBy: auth.currentUser?.uid,
                createdByName: importedByName,
                createdByEmail: importedByEmail,
              });
            }

            const savedQuotes: Quote[] = [];
            for (const quote of newQuotes) {
              if (isLocal) {
                const response = await localApiRequest(`/api/organizations/${orgId}/documents`, {
                  method: 'POST',
                  body: JSON.stringify(quote),
                });
                savedQuotes.push(normalizeQuoteRecord(localApiRecord<Quote>(response)));
              } else {
                const documentRef = await addDoc(collection(db, 'documents'), {
                  ...quote,
                  createdAt: serverTimestamp(),
                });
                await Logger.log(orgId, 'CREATE', 'Quotes', documentRef.id, String(quote.number), {
                  after: quote,
                  details: `Quote ${quote.number} imported`,
                });
                savedQuotes.push(normalizeQuoteRecord({
                  ...quote,
                  id: documentRef.id,
                  createdAt: new Date().toISOString(),
                } as Quote));
              }
            }

            if (isLocal) {
              setQuotes(current => [...savedQuotes, ...current].sort((a, b) => b.date.localeCompare(a.date)));
            } else if (nextNumberCandidate !== configuredNextNumber) {
              updateDoc(doc(db, 'settings', orgId), { nextQuoteNumber: nextNumberCandidate })
                .catch(error => console.warn('Could not update the next quote number after CSV import:', error));
            }
            setNextQuoteNumber(nextNumberCandidate);
            notify.confirm(`Successfully imported ${savedQuotes.length} quote${savedQuotes.length === 1 ? '' : 's'}`, { id: toastId });
          } catch (error) {
            console.error('Quote CSV import failed:', error);
            const message = error instanceof Error ? error.message : 'Failed to import quotes';
            notify.error(message, { id: toastId });
          }
        },
        error: error => {
          console.error('Quote CSV read failed:', error);
          notify.error(`Could not read the CSV file: ${error.message}`);
        },
      });
    };
    input.click();
  };

  const handleExportCSV = () => {
    const exportData = filteredQuotes.flatMap(quote => 
      (quote.items || []).map(item => ({
        '*ContactName': quote.contactName || '',
        '*QuoteNumber': quote.number,
        'Reference': quote.reference || '',
        '*Date': quote.date,
        '*ExpiryDate': quote.expiryDate,
        '*Description': item.description,
        '*Quantity': item.quantity,
        '*UnitAmount': item.unitPrice,
        '*AccountCode': item.account,
        'Quotation Value': quote.totalAmount
      }))
    );

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `quotes_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadTemplate = () => {
    const headers = ['*ContactName', '*QuoteNumber', 'Reference', '*Date', '*ExpiryDate', '*Description', '*Quantity', '*UnitAmount', '*AccountCode'];
    const csv = Papa.unparse([headers]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'quote_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    const isLocal = hasLocalSession();
    if ((!auth.currentUser && !isLocal) || isSaving) return;

    if (!newQuote.contactId) {
      alert("Please select a contact before saving.");
      return;
    }

    if (!newQuote.date || !newQuote.expiryDate) {
      alert("Please ensure both Date and Expiry Date are filled.");
      return;
    }

    const hasValidItems = (newQuote.items || []).some(item => item.description.trim() !== '');
    if (!hasValidItems) {
      alert("Please add at least one item with a description.");
      return;
    }

    setIsSaving(true);
    
    const totalAmount = newQuote.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const itemsWithTotal = (newQuote.items || []).map(item => normalizeQuoteLine({
      ...item,
      total: calculateItemTotal(item)
    }));

    const contact = contacts.find(c => c.id === newQuote.contactId);
    const contactName = contact?.name || 'Walk-in Customer';
    const contactEmail = contact?.email || (contact?.emails && contact?.emails.length > 0 ? contact?.emails[0] : '');
    const fullQuoteNumber = newQuote.number;
    
    let actionDetails = `${isEditing ? 'Updated' : 'Created'} Quote ${fullQuoteNumber} for ${contactName} with Amount ${totalAmount.toLocaleString()}`;
    
    if (isEditing && selectedQuote) {
      const changes = [];
      if (selectedQuote.status !== newQuote.status) {
        changes.push(`Status changed to ${newQuote.status}`);
      }
      if (Math.abs(selectedQuote.totalAmount - totalAmount) > 0.01) {
        changes.push(`Total changed from ${selectedQuote.totalAmount.toLocaleString()} to ${totalAmount.toLocaleString()}`);
      }
      if (changes.length > 0) {
        actionDetails = `Updated Quote ${fullQuoteNumber}: ${changes.join(', ')}`;
      }
    }

    const actionEntry: ActionLogEntry = {
      action: isEditing ? 'Updated' : 'Created',
      user: auth.currentUser?.displayName || 'Unknown User',
      email: auth.currentUser?.email || 'No Email',
      timestamp: new Date().toISOString(),
      details: actionDetails
    };

    try {
      if (isLocal) {
        const localDocument = {
          ...newQuote,
          expiryDate: newQuote.expiryDate,
          dueDate: newQuote.expiryDate,
          contactName,
          contactEmail,
          items: itemsWithTotal,
          totalAmount,
          type: (isRepeatingQuote ? 'repeating_quote' : 'quote') as Quote['type'],
          status: isRepeatingQuote ? 'Draft' : newQuote.status,
          ...(isRepeatingQuote ? { repeatingConfig } : {}),
          actionDetails,
        };
        const response = isEditing && selectedQuote
          ? await localApiRequest(`/api/organizations/${orgId}/documents/${selectedQuote.id}`, {
              method: 'PATCH',
              body: JSON.stringify(localDocument),
            })
          : await localApiRequest(`/api/organizations/${orgId}/documents`, {
              method: 'POST',
              body: JSON.stringify(localDocument),
            });
        const savedQuote = normalizeQuoteRecord(localApiRecord<Quote>(response));
        const quoteWithId = normalizeQuoteRecord({ ...localDocument, ...savedQuote, id: savedQuote.id || selectedQuote?.id } as Quote);
        let generatedQuotes: Quote[] = [];
        if (isRepeatingQuote) {
          const generationResponse = await localApiRequest<{ generated?: Quote[] }>(`/api/organizations/${orgId}/repeating-quotes/generate`, {
            method: 'POST',
            body: JSON.stringify({ throughDate: new Date().toISOString().split('T')[0] }),
          });
          generatedQuotes = Array.isArray(generationResponse?.generated)
            ? generationResponse.generated.map(normalizeQuoteRecord)
            : [];
        }
        setQuotes(current => {
          const withSavedQuote = isEditing
            ? current.map(quote => quote.id === quoteWithId.id ? quoteWithId : quote)
            : [quoteWithId, ...current];
          return [...generatedQuotes, ...withSavedQuote];
        });
        setSelectedQuote(quoteWithId);
        notify.confirm(isEditing ? 'Quote updated successfully' : 'Quote created successfully');
      } else if (isEditing && selectedQuote) {
        const docData = {
          ...newQuote,
          expiryDate: newQuote.expiryDate,
          dueDate: newQuote.expiryDate,
          type: isRepeatingQuote ? 'repeating_quote' : newQuote.type,
          status: isRepeatingQuote ? 'Draft' : newQuote.status,
          ...(isRepeatingQuote ? { repeatingConfig } : {}),
          contactName,
          contactEmail,
          items: itemsWithTotal,
          totalAmount,
          actionDetails,
          actionLog: arrayUnion(actionEntry)
        };
        await updateDoc(doc(db, 'documents', selectedQuote.id), docData);
        await Logger.logEdit(orgId, 'Quotes', selectedQuote.id, selectedQuote.number, selectedQuote, docData);

        setSelectedQuote(prev => prev ? { 
          ...prev, 
          ...docData,
          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
        } as any : null);
        
        notify.confirm("Quote updated successfully");
      } else {
        const docData = {
          ...newQuote,
          expiryDate: newQuote.expiryDate,
          dueDate: newQuote.expiryDate,
          orgId,
          number: fullQuoteNumber,
          contactName,
          contactEmail,
          items: itemsWithTotal,
          totalAmount,
          type: isRepeatingQuote ? 'repeating_quote' : 'quote',
          status: isRepeatingQuote ? 'Draft' : newQuote.status,
          ...(isRepeatingQuote ? { repeatingConfig } : {}),
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          actionDetails,
          actionLog: [actionEntry],
          createdAt: serverTimestamp()
        };

        const docRef = await addDoc(collection(db, 'documents'), docData);
        const trackedLines = (itemsWithTotal as any[]).filter(line => Boolean(line.trackInventory)).map(line => ({
          quoteLineId: line.quoteLineId || line.id,
          productId: line.productId || line.itemId,
          itemId: line.productId || line.itemId,
          itemCode: line.item,
          description: line.description,
          quantityOrdered: Number(line.quantity || 0),
          quantityShipped: Number(line.quantity || 0),
          qtyDone: Number(line.quantity || 0),
          uomId: line.uomId || '',
        }));
        if (trackedLines.length) {
          await addDoc(collection(db, 'documents'), {
            orgId, type: 'delivery', status: 'draft', number: `DLV-DRAFT-${fullQuoteNumber}`,
            sourceQuoteId: docRef.id, sourceQuoteNumber: fullQuoteNumber, contactId: newQuote.contactId,
            contactName, date: new Date().toISOString().split('T')[0], items: trackedLines,
            internalNote: `Source: ${fullQuoteNumber}`, deliveryStatus: 'to_deliver', createdAt: serverTimestamp(),
          });
        }
        await Logger.log(orgId, 'CREATE', 'Quotes', docRef.id, fullQuoteNumber, {
          after: docData,
          details: actionDetails,
        });
        if (['Accepted', 'Approved'].includes(String(docData.status))) {
          await Logger.log(orgId, 'APPROVE', 'Quotes', docRef.id, fullQuoteNumber, {
            to: docData.status,
            details: `Quote ${fullQuoteNumber} approved`,
          });
        }

        setSelectedQuote(normalizeQuoteRecord({
          ...docData,
          id: docRef.id,
          createdAt: new Date().toISOString()
        } as any));

        // Increment next quote number in settings
        const settingsRef = doc(db, 'settings', orgId);
        const prefix = orgSettings?.quotePrefix || 'QT-';
        let nextNum = nextQuoteNumber + 1;

        // If manual number was used, try to extract the next number from it
        if (newQuote.number.startsWith(prefix)) {
          const numPart = parseInt(String(newQuote?.number || '').replace(prefix, ''));
          if (!isNaN(numPart)) {
            nextNum = numPart + 1;
          }
        }

        await updateDoc(settingsRef, {
          nextQuoteNumber: nextNum
        });
        setNextQuoteNumber(nextNum);
      }

      setIsModalOpen(false);
      setIsEditing(false);
      setIsRepeatingQuote(false);
      setIsSaving(false);
      
      const prefix = orgSettings?.quotePrefix || 'QT-';
      const nextNum = isEditing ? nextQuoteNumber : nextQuoteNumber + 1;

      setNewQuote({
        number: `${prefix}${nextNum}`,
        contactId: '',
        contactName: '',
        contactEmail: '',
        date: new Date().toISOString().split('T')[0],
        expiryDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        reference: '',
        currency: currencies[0],
        brandingTheme: 'Standard',
        totalAmount: 0,
        items: [{ 
          id: createQuoteLineId(),
          quoteLineId: createQuoteLineId(),
          item: '', 
          description: '', 
          quantity: 1, 
          unitPrice: 0,
          discount: 0,
          discountType: 'percentage',
          total: 0,
          account: '',
          taxRate: '0%',
          trackingCategory: ''
        }],
        status: 'Draft',
        termsAndConditions: '',
        billingAddress: '',
        deliveryAddress: '',
        trackingCategory: '',
        type: 'quote',
        notes: '',
        tags: []
      });
    } catch (error) {
      console.error("Error saving quote:", error);
      setIsSaving(false);
      notify.error(error instanceof Error ? error.message : "Failed to save quote");
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const contact = hasLocalSession()
        ? localApiRecord<any>(await localApiRequest(`/api/organizations/${orgId}/contacts`, {
            method: 'POST',
            body: JSON.stringify({
              ...newContact,
              type: 'Customer',
            }),
          }))
        : {
            id: (await addDoc(collection(db, 'contacts'), {
              ...newContact,
              orgId,
              createdAt: serverTimestamp()
            })).id,
            ...newContact,
          };

      if (!contact?.id) throw new Error('Contact was created without an ID');

      setContacts(prev => [contact, ...prev.filter(existing => existing.id !== contact.id)]);
      setNewQuote(prev => ({
        ...prev,
        contactId: contact.id,
        contactName: contact.name || '',
        contactEmail: contact.email || (Array.isArray(contact.emails) ? contact.emails[0] || '' : ''),
      }));
      setIsContactModalOpen(false);
      setNewContact({ name: '', email: '', type: 'customer' });
      notify.confirm(language === 'ar' ? 'تمت إضافة جهة الاتصال' : 'Contact added successfully');
    } catch (err) {
      console.error('Error creating contact:', err);
      notify.error(language === 'ar' ? 'تعذر إضافة جهة الاتصال' : 'Failed to add contact');
    }
  };

  const handleAddCurrency = (e: React.FormEvent) => {
    e.preventDefault();
    if (customCurrency && !currencies.includes(customCurrency.toUpperCase())) {
      const newCurr = customCurrency.toUpperCase();
      setCurrencies(prev => [...prev, newCurr]);
      setNewQuote(prev => ({ ...prev, currency: newCurr }));
    }
    setIsCurrencyModalOpen(false);
    setCustomCurrency('');
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccount.code || !newAccount.name) {
      notify.error("Please fill in all required fields");
      return;
    }

    try {
      const path = 'chart_of_accounts';
      const inferredNature = (['Bank', 'Current Asset', 'Fixed Asset', 'Non-current Asset', 'Direct Cost', 'Expense'].includes(newAccount.type || '')) ? 'Debit' : 'Credit';
      const userName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Unknown User';
      const userEmail = auth.currentUser?.email || 'No Email';

      await addDoc(collection(db, path), {
        ...newAccount,
        nature: inferredNature,
        orgId,
        status: 'Active',
        createdBy: auth.currentUser?.uid,
        createdAt: serverTimestamp(),
        actionLog: [
          {
            type: 'create',
            by: `${userName} (${userEmail})`,
            at: new Date().toISOString()
          }
        ]
      });
      
      // Update the current item with the new account
      if (activeAccountDropdown !== null) {
        updateItem(activeAccountDropdown, 'account', newAccount.code);
      }
      
      setIsAccountModalOpen(false);
      setActiveAccountDropdown(null);
      setNewAccount({ code: '', name: '', type: 'Revenue', description: '', taxRate: '0%' });
      notify.confirm("Account added successfully");
    } catch (err) {
      console.error(err);
      notify.error("Failed to add account");
    }
  };

  const addItem = () => {
    setNewQuote(prev => ({
      ...prev,
      items: [...prev.items, normalizeQuoteLine({
        item: '', 
        description: '', 
        quantity: 1, 
        unitPrice: 0,
        discount: 0,
        discountType: 'percentage',
        total: 0,
        account: '',
        taxRate: '0%',
        trackingCategory: ''
      })]
    }));
  };

  const removeItem = (index: number) => {
    if (newQuote.items.length <= 1) {
      notify.error("At least one item is required");
      return;
    }
    setNewQuote(prev => ({
      ...prev,
      items: (prev.items || []).filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...newQuote.items];
    let updatedItem = { ...newItems[index], [field]: value };
    
    // Recalculate item total
    updatedItem.total = calculateItemTotal(updatedItem);
    
    if (field === 'item' && value) {
      const selectedItem = itemsList.find(i => i.code === value);
      if (selectedItem) {
        updatedItem = { 
          ...updatedItem, 
          item: selectedItem.code,
          description: selectedItem.description || selectedItem.name,
          unitPrice: selectedItem.unitPrice,
          taxRate: selectedItem.taxRate || '0%',
          productId: selectedItem.id || null,
          itemId: selectedItem.id || null,
          trackInventory: Boolean(selectedItem.trackInventory),
          uomId: selectedItem.salesUomId || selectedItem.uomId || selectedItem.stockUomId || '',
        };
        updatedItem.total = calculateItemTotal(updatedItem);
      }
    }
    
    newItems[index] = updatedItem;
    setNewQuote(prev => ({ ...prev, items: newItems }));
  };

  const requestDecline = (quote: Quote) => setQuoteToDecline(quote);

  const handleConfirmDecline = async (reason: string) => {
    if (!quoteToDecline) return;
    setIsDeclineSaving(true);
    try {
      await handleUpdateStatus(quoteToDecline.id, 'Declined', reason);
      setQuoteToDecline(null);
    } finally {
      setIsDeclineSaving(false);
    }
  };

  const handleUpdateStatus = async (quoteId: string, newStatus: Quote['status'], statusReason?: string) => {
    if (hasLocalSession()) {
      if (newStatus !== 'Invoiced') {
        try {
          const response = await localApiRequest(`/api/organizations/${orgId}/documents/${quoteId}`, {
            method: 'PATCH',
            body: JSON.stringify({ status: newStatus, ...(statusReason ? { declineReason: statusReason } : {}) }),
          });
          const updatedQuote = localApiRecord<Partial<Quote>>(response);

          setQuotes(current => current.map(quote =>
            quote.id === quoteId ? { ...quote, ...updatedQuote, status: newStatus } : quote
          ));
          setSelectedQuote(current => current?.id === quoteId
            ? { ...current, ...updatedQuote, status: newStatus }
            : current
          );
          notify.confirm(newStatus === 'Sent' ? 'Quote sent successfully' : `Quote marked as ${newStatus}`);
        } catch (error) {
          console.error('Error updating quote status in local mode:', error);
          notify.error('Failed to update quote status');
          if (statusReason) throw error;
        }
        return;
      }

      try {
        const result = await localApiRequest<{
          invoiceId?: string;
          invoice?: { id?: string };
          data?: { invoiceId?: string; invoice?: { id?: string } };
        }>(`/api/organizations/${orgId}/documents/${quoteId}/convert-to-invoice`, {
          method: 'POST',
        });
        const invoiceId = result.invoiceId || result.invoice?.id || result.data?.invoiceId || result.data?.invoice?.id;

        setQuotes(current => current.map(quote =>
          quote.id === quoteId ? { ...quote, status: 'Converted', invoiceId } : quote
        ));
        setSelectedQuote(current => current?.id === quoteId
          ? { ...current, status: 'Converted', invoiceId }
          : current
        );
        notify.confirm('Quote converted to invoice successfully');

        if (invoiceId && onNavigate) {
          onNavigate('invoices', invoiceId);
        }
      } catch (error) {
        console.error('Error converting quote to invoice:', error);
        notify.error('Failed to convert quote to invoice');
      }
      return;
    }
    if (!auth.currentUser) return;
    try {
      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) return;

      const actionDetails = `Quote ${quote.number} status changed to ${newStatus} by ${auth.currentUser.displayName || 'Unknown User'}${statusReason ? `: ${statusReason}` : ''}`;
      
      const actionEntry: ActionLogEntry = {
        action: 'Status Updated',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: actionDetails
      };

      await updateDoc(doc(db, 'documents', quoteId), {
        status: newStatus,
        ...(statusReason ? { declineReason: statusReason } : {}),
        updatedAt: serverTimestamp(),
        actionDetails: actionDetails,
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'STATUS_CHANGE', 'Quotes', quoteId, quote.number, {
        from: quote.status,
        to: newStatus,
        details: actionDetails,
        ...(statusReason ? { reason: statusReason } : {}),
      });

      // If status is changed to 'Invoiced', create a draft invoice
      if (newStatus === 'Invoiced') {
        const prefix = orgSettings?.invoicePrefix || 'INV-';
        const nextNum = orgSettings?.nextInvoiceNumber || 1;
        const invoiceNumber = `${prefix}${nextNum}`;
        const invoiceActionDetails = `Invoice ${invoiceNumber} created from Quote ${quote.number}`;
        
        const invoiceActionEntry: ActionLogEntry = {
          action: 'Created',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || 'No Email',
          timestamp: new Date().toISOString(),
          details: invoiceActionDetails
        };

        const invoiceRef = await addDoc(collection(db, 'documents'), {
          number: invoiceNumber,
          contactId: quote.contactId,
          contactName: quote.contactName || 'Walk-in Customer',
          contactEmail: (quote as any).contactEmail || '',
          date: new Date().toISOString().split('T')[0],
          dueDate: quote.expiryDate,
          reference: quote.reference || quote.number,
          currency: quote.currency,
          brandingTheme: quote.brandingTheme || 'Standard',
          amountsAre: 'Tax Exclusive',
          totalAmount: quote.totalAmount,
          status: 'Draft',
          items: quote.items,
          tags: quote.tags || [],
          termsAndConditions: quote.termsAndConditions || '',
          type: 'invoice',
          orgId,
          createdBy: auth.currentUser.uid,
          createdByName: auth.currentUser.displayName || 'Unknown User',
          createdByEmail: auth.currentUser.email || 'No Email',
          actionDetails: invoiceActionDetails,
          actionLog: [invoiceActionEntry],
          createdAt: serverTimestamp(),
          quoteId: quote.id // Keep a reference to the source quote
        });
        await Logger.log(orgId, 'CREATE', 'Invoices', invoiceRef.id, invoiceNumber, {
          details: invoiceActionDetails,
        });

        // Increment next invoice number in settings
        const settingsRef = doc(db, 'settings', orgId);
        await updateDoc(settingsRef, {
          nextInvoiceNumber: increment(1)
        });

        // Update the quote with the invoice ID
        await updateDoc(doc(db, 'documents', quoteId), {
          invoiceId: invoiceRef.id,
          updatedAt: serverTimestamp(),
          actionLog: arrayUnion({
            action: 'Invoice Created',
            user: auth.currentUser?.displayName || 'Unknown User',
            email: auth.currentUser?.email || 'No Email',
            timestamp: new Date().toISOString(),
            details: `Invoice ${invoiceNumber} created from this Quote`,
            linkId: invoiceRef.id,
            linkType: 'invoice'
          })
        });
        await Logger.log(orgId, 'EDIT', 'Quotes', quoteId, quote.number, {
          details: `Invoice ${invoiceNumber} created from this quote`,
          invoiceId: invoiceRef.id,
        });

        notify.confirm(`Draft invoice ${invoiceNumber} created successfully`, {
          action: onNavigate ? {
            label: 'View Invoice',
            onClick: () => onNavigate('invoices', invoiceRef.id)
          } : undefined
        });
      }

      if (selectedQuote?.id === quoteId) {
        setSelectedQuote(prev => prev ? { 
          ...prev, 
          status: newStatus,
          ...(statusReason ? { declineReason: statusReason } : {}),
          actionDetails: actionDetails,
          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
        } : null);
      }
    } catch (error) {
      console.error("Error updating quote status:", error);
      notify.error("Failed to update status");
      if (statusReason) throw error;
    }
  };

  const handleConfirmPartialInvoice = async () => {
    if (!selectedQuote) return;
    if (!hasLocalSession() && !auth.currentUser) return;
    
    // Validation
    const invalidItem = partialInvoiceItems.find(item => 
      item.qtyToInvoiceNow > (item.quantity - (item.invoicedQuantity || 0)) || item.qtyToInvoiceNow < 0
    );
    
    if (invalidItem) {
      notify.error(`Invalid quantity for ${invalidItem.item || 'item'}. Cannot exceed remaining quantity.`);
      return;
    }

    const itemsToInvoice = partialInvoiceItems.filter(item => item.qtyToInvoiceNow > 0);
    if (itemsToInvoice.length === 0) {
      notify.error("Please specify at least one item quantity to invoice.");
      return;
    }

    setIsPartialSaving(true);
    try {
      if (hasLocalSession()) {
        const result = await localApiRequest<{
          invoiceId: string;
          invoice: any;
          quote: Quote;
        }>(`/api/organizations/${orgId}/documents/${selectedQuote.id}/convert-to-invoice`, {
          method: 'POST',
          body: JSON.stringify({
            partialItems: partialInvoiceItems
              .map((item, index) => ({ index, quantity: Number(item.qtyToInvoiceNow || 0) }))
              .filter(item => item.quantity > 0),
          }),
        });

        setQuotes(current => current.map(quote => quote.id === result.quote.id ? result.quote : quote));
        setSelectedQuote(result.quote);
        setAllInvoices(current => [result.invoice, ...current.filter(invoice => invoice.id !== result.invoiceId)]);
        setIsPartialInvoiceModalOpen(false);
        notify.confirm(`Partial Invoice ${result.invoice.number} created successfully`, {
          action: onNavigate ? {
            label: 'View Invoice',
            onClick: () => onNavigate('invoices', result.invoiceId)
          } : undefined
        });
        return;
      }

      const prefix = orgSettings?.invoicePrefix || 'INV-';
      const nextNum = orgSettings?.nextInvoiceNumber || 1;
      const invoiceNumber = `${prefix}${nextNum}`;
      
      const invoiceItems = itemsToInvoice.map(item => ({
        ...item,
        quantity: item.qtyToInvoiceNow,
        total: (item.qtyToInvoiceNow * item.unitPrice) * (1 - (item.discount || 0) / 100)
      }));

      const subtotalAmount = invoiceItems.reduce((sum, item) => sum + item.total, 0);
      const totalTaxAmount = invoiceItems.reduce((sum, item) => sum + (item.total * parseTaxRate(item.taxRate)), 0);
      const totalInvoiceAmount = subtotalAmount + totalTaxAmount;

      const invoiceActionDetails = `Partial Invoice ${invoiceNumber} created from Quote ${selectedQuote.number}`;
      const invoiceActionEntry: ActionLogEntry = {
        action: 'Created',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: invoiceActionDetails
      };

      // Create the Invoice
      const invoiceRef = await addDoc(collection(db, 'documents'), {
        number: invoiceNumber,
        contactId: selectedQuote.contactId,
        contactName: selectedQuote.contactName || 'Walk-in Customer',
        contactEmail: (selectedQuote as any).contactEmail || '',
        date: new Date().toISOString().split('T')[0],
        dueDate: selectedQuote.expiryDate,
        reference: selectedQuote.reference || selectedQuote.number,
        currency: selectedQuote.currency,
        brandingTheme: selectedQuote.brandingTheme || 'Standard',
        amountsAre: 'Tax Exclusive',
        totalAmount: totalInvoiceAmount,
        status: 'Draft',
        items: invoiceItems,
        tags: selectedQuote.tags || [],
        termsAndConditions: selectedQuote.termsAndConditions || '',
        type: 'invoice',
        orgId,
        createdBy: auth.currentUser.uid,
        createdByName: auth.currentUser.displayName || 'Unknown User',
        createdByEmail: auth.currentUser.email || 'No Email',
        actionDetails: invoiceActionDetails,
        actionLog: [invoiceActionEntry],
        createdAt: serverTimestamp(),
        quoteId: selectedQuote.id
      });

      // Update Quotation Items
      const updatedQuoteItems = (selectedQuote.items || []).map(origItem => {
        const partial = partialInvoiceItems.find(p => p.id === origItem.id || (p.item === origItem.item && p.description === origItem.description));
        if (partial) {
          return {
            ...origItem,
            invoicedQuantity: (origItem.invoicedQuantity || 0) + partial.qtyToInvoiceNow
          };
        }
        return origItem;
      });

      const isFullyInvoiced = updatedQuoteItems.every(item => (item.invoicedQuantity || 0) >= item.quantity);
      const newStatus = isFullyInvoiced ? 'Invoiced' : 'Accepted';

      await updateDoc(doc(db, 'documents', selectedQuote.id), {
        items: updatedQuoteItems,
        status: newStatus,
        updatedAt: serverTimestamp(),
        actionLog: arrayUnion({
          action: 'Partial Invoice Created',
          user: auth.currentUser?.displayName || 'Unknown User',
          email: auth.currentUser?.email || 'No Email',
          timestamp: new Date().toISOString(),
          details: `Created partial invoice ${invoiceNumber} for selected items.`,
          linkId: invoiceRef.id,
          linkType: 'invoice'
        })
      });

      // Increment next invoice number
      await updateDoc(doc(db, 'settings', orgId), {
        nextInvoiceNumber: increment(1)
      });

      notify.confirm(`Partial Invoice ${invoiceNumber} created successfully`, {
        action: onNavigate ? {
          label: 'View Invoice',
          onClick: () => onNavigate('invoices', invoiceRef.id)
        } : undefined
      });
      setIsPartialInvoiceModalOpen(false);
      
      setSelectedQuote(prev => prev ? {
        ...prev,
        items: updatedQuoteItems,
        status: newStatus
      } : null);

    } catch (error) {
      console.error("Error creating partial invoice:", error);
      notify.error("Failed to create partial invoice");
    } finally {
      setIsPartialSaving(false);
    }
  };

  const handleCopyQuote = async (targetType: 'quote' | 'invoice' | 'purchase_order' | 'bill') => {
    if (!selectedQuote || isCopying) return;

    setIsCopying(true);
    setShowCopyToMenu(false);
    const today = new Date().toISOString().split('T')[0];
    const targetLabels = {
      quote: 'quote', invoice: 'invoice', purchase_order: 'purchase order', bill: 'bill'
    } as const;
    const targetTab = targetType === 'quote' ? 'quotes' : targetType === 'invoice' ? 'invoices' : targetType === 'bill' ? 'bills' : 'purchase_orders';

    try {
      if (hasLocalSession()) {
        const result = await localApiRequest<{ documentId?: string; id?: string; number?: string; document?: Quote }>(
          `/api/organizations/${orgId}/documents/${selectedQuote.id}/copy-to`,
          { method: 'POST', body: JSON.stringify({ targetType }) }
        );
        const createdId = result.documentId || result.id;
        if (!createdId) throw new Error('The copied document was not returned');
        notify.confirm(`${targetLabels[targetType]} ${result.number || ''} created successfully`);
        if (targetType === 'quote' && result.document) {
          setQuotes(current => [result.document!, ...current]);
          setSelectedQuote(result.document);
        } else {
          setSelectedQuote(null);
          onNavigate?.(targetTab, createdId);
        }
        return;
      }

      if (!auth.currentUser) throw new Error('You must be signed in to copy a quote');
      const quote = selectedQuote;
      const settings = orgSettings || {};
      const numberConfig: Record<string, [string, number | undefined]> = {
        quote: [settings.quotePrefix || 'QT-', settings.nextQuoteNumber],
        invoice: [settings.invoicePrefix || 'INV-', settings.nextInvoiceNumber],
        purchase_order: [settings.purchaseOrderPrefix || settings.purchase_order_prefix || 'PO-', settings.nextPurchaseOrderNumber || settings.next_purchase_order_number],
        bill: [settings.billPrefix || settings.purchaseBillPrefix || 'BILL-', settings.nextBillNumber || settings.nextPurchaseBillNumber],
      };
      const [prefix, nextNumber] = numberConfig[targetType];
      const number = `${prefix}${nextNumber || Date.now()}`;
      const { id: _sourceId, actionLog: _sourceActionLog, invoiceId: _invoiceId, purchaseOrderId: _purchaseOrderId, source: _source, ...sourceData } = quote;
      const copyData: any = {
        ...sourceData,
        number, type: targetType, status: 'Draft', date: today,
        ...(targetType === 'quote'
          ? { expiryDate: quote.expiryDate, dueDate: quote.expiryDate }
          : { dueDate: quote.expiryDate }),
        ...(targetType === 'purchase_order' ? { deliveryDate: quote.expiryDate } : {}),
        amountsAre: (quote as any).amountsAre || 'Tax Exclusive', source: 'quote-copy', sourceQuoteId: quote.id,
        ...(targetType === 'invoice' ? { quoteId: quote.id } : {}),
        createdBy: auth.currentUser.uid, createdByName: auth.currentUser.displayName || 'Unknown User',
        createdByEmail: auth.currentUser.email || 'No Email', createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
        actionLog: [{ action: 'Created', user: auth.currentUser.displayName || 'Unknown User', email: auth.currentUser.email || 'No Email', timestamp: new Date().toISOString(), details: `${targetLabels[targetType]} copied from Quote ${quote.number}` }],
      };
      const copiedRef = await addDoc(collection(db, 'documents'), copyData);
      await Logger.log(orgId, 'CREATE', targetLabels[targetType], copiedRef.id, number, {
        after: copyData,
        details: `${targetLabels[targetType]} copied from Quote ${quote.number}`,
      });
      const settingsField = { quote: 'nextQuoteNumber', invoice: 'nextInvoiceNumber', purchase_order: 'nextPurchaseOrderNumber', bill: 'nextBillNumber' }[targetType];
      if (nextNumber && settingsField) await updateDoc(doc(db, 'settings', orgId), { [settingsField]: increment(1) });
      notify.confirm(`${targetLabels[targetType]} ${number} created successfully`);
      if (targetType === 'quote') {
        setSelectedQuote({ id: copiedRef.id, ...copyData } as Quote);
      } else {
        setSelectedQuote(null);
        onNavigate?.(targetTab, copiedRef.id);
      }
    } catch (error) {
      console.error('Error copying quote:', error);
      notify.error(`Failed to copy quote as ${targetLabels[targetType]}`);
    } finally {
      setIsCopying(false);
    }
  };

  const handleDeleteQuote = async (quoteId: string) => {
    try {
      if (hasLocalSession()) {
        const deletedAt = new Date().toISOString();
        const response = await localApiRequest(`/api/organizations/${orgId}/documents/${quoteId}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'Deleted', deletedAt }),
        });
        const updatedQuote = localApiRecord<Partial<Quote>>(response);
        setQuotes(current => current.map(quote =>
          quote.id === quoteId ? { ...quote, ...updatedQuote, status: 'Deleted', deletedAt } : quote
        ));
        setSelectedQuote(current => current?.id === quoteId
          ? { ...current, ...updatedQuote, status: 'Deleted', deletedAt }
          : current
        );
        notify.confirm('Quote deleted successfully');
        return;
      }

      if (!auth.currentUser) return;
      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) return;

      const actionDetails = `Quote ${quote.number} deleted by ${auth.currentUser.displayName || 'Unknown User'} on ${new Date().toLocaleString()}`;
      
      const actionEntry: ActionLogEntry = {
        action: 'Deleted',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: actionDetails
      };

      await updateDoc(doc(db, 'documents', quoteId), {
        status: 'Deleted',
        updatedAt: serverTimestamp(),
        deletedBy: auth.currentUser.uid,
        deletedByName: auth.currentUser.displayName || 'Unknown User',
        deletedByEmail: auth.currentUser.email || 'No Email',
        deletedAt: serverTimestamp(),
        actionDetails: actionDetails,
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'STATUS_CHANGE', 'Quotes', quoteId, quote.number, {
        from: quote.status,
        to: 'Deleted',
        details: actionDetails,
      });
      
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote(prev => prev ? { 
          ...prev, 
          status: 'Deleted',
          deletedByName: auth.currentUser?.displayName || 'Unknown User',
          deletedByEmail: auth.currentUser?.email || 'No Email',
          actionDetails: actionDetails,
          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
        } : null);
      }
      notify.confirm("Quote deleted successfully");
    } catch (error) {
      console.error("Error deleting quote:", error);
      notify.error("Failed to delete quote");
    }
  };

  const handleRestoreQuote = async (quoteId: string) => {
    if (!auth.currentUser) return;
    try {
      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) return;

      const actionDetails = `Quote ${quote.number} restored to Draft by ${auth.currentUser.displayName || 'Unknown User'}`;
      
      const actionEntry: ActionLogEntry = {
        action: 'Restored',
        user: auth.currentUser?.displayName || 'Unknown User',
        email: auth.currentUser?.email || 'No Email',
        timestamp: new Date().toISOString(),
        details: actionDetails
      };

      await updateDoc(doc(db, 'documents', quoteId), {
        status: 'Draft',
        updatedAt: serverTimestamp(),
        deletedBy: null,
        deletedByName: null,
        deletedByEmail: null,
        deletedAt: null,
        actionDetails: actionDetails,
        actionLog: arrayUnion(actionEntry)
      });
      await Logger.log(orgId, 'EDIT', 'Quotes', quoteId, quote.number, {
        from: quote.status,
        to: 'Draft',
        details: actionDetails,
      });
      
      if (selectedQuote?.id === quoteId) {
        setSelectedQuote(prev => prev ? { 
          ...prev, 
          status: 'Draft',
          deletedByName: undefined,
          deletedByEmail: undefined,
          deletedAt: undefined,
          actionDetails: actionDetails,
          actionLog: prev.actionLog ? [...prev.actionLog, actionEntry] : [actionEntry]
        } : null);
      }
      notify.confirm("Quote restored to Draft successfully");
    } catch (error) {
      console.error("Error restoring quote:", error);
      notify.error("Failed to restore quote");
    }
  };

  const handleEditQuote = () => {
    if (!selectedQuote) return;
    if (getQuoteStage(selectedQuote) === 'Accepted') {
      notify.error('Accepted quotes cannot be edited.');
      return;
    }
    if (getLinkedInvoicesForQuote(selectedQuote).length > 0) {
      notify.error('This quote has an invoice linked already.');
      return;
    }
    setIsRepeatingQuote(selectedQuote.type === 'repeating_quote');
    if (selectedQuote.repeatingConfig) setRepeatingConfig(selectedQuote.repeatingConfig);
    setNewQuote({
      number: selectedQuote.number,
      contactId: selectedQuote.contactId,
      contactName: selectedQuote.contactName || '',
      contactEmail: (selectedQuote as any).contactEmail || '',
      date: selectedQuote.date,
      expiryDate: selectedQuote.expiryDate,
      reference: selectedQuote.reference || '',
      currency: selectedQuote.currency,
      brandingTheme: selectedQuote.brandingTheme || 'Standard',
      totalAmount: selectedQuote.totalAmount,
      items: (selectedQuote.items || []).map(item => ({
        ...item,
        account: item.account || '',
        taxRate: item.taxRate || '0%',
        trackingCategory: item.trackingCategory || ''
      })),
      status: selectedQuote.status,
      termsAndConditions: selectedQuote.termsAndConditions || '',
      billingAddress: selectedQuote.billingAddress || '',
      deliveryAddress: selectedQuote.deliveryAddress || '',
      trackingCategory: selectedQuote.trackingCategory || '',
      type: selectedQuote.type || 'quote',
      notes: selectedQuote.notes || '',
      tags: selectedQuote.tags || []
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDownloadPDF = async () => {
    if (!selectedQuote) return;

    try {
      await generateQuotePdf({
        quote: selectedQuote,
        organization,
        contact: contacts.find(c => c.id === selectedQuote.contactId),
        calculateSubtotal: calculateItemSubtotal,
        calculateTax: calculateItemTax,
      });
      notify.confirm('PDF downloaded successfully');
    } catch (err) {
      console.error('PDF Generation Error Details:', err);
      notify.error(`Failed to generate PDF: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedQuote) return;

    let contact = contacts.find(c => c.id === selectedQuote.contactId);
    
    // If contact not found in local state (e.g. due to type filters), fetch directly from Firestore
    if (!contact && selectedQuote.contactId) {
      try {
        const contactDoc = await getDoc(doc(db, 'contacts', selectedQuote.contactId));
        if (contactDoc.exists()) {
          contact = { id: contactDoc.id, ...contactDoc.data() } as any;
        }
      } catch (error) {
        console.error("Error fetching contact for email:", error);
      }
    }

    const recipientEmails = getQuoteRecipientEmails(contact, selectedQuote.contactEmail);

    if (recipientEmails.length === 0) {
      notify.error("Contact email missing");
      return;
    }
    
    const itemsList = (selectedQuote.items || []).map(item => 
      `• ${item.description} (${item.quantity} x ${selectedQuote.currency} ${(item.unitPrice || 0).toLocaleString()}): ${selectedQuote.currency} ${(item.total || 0).toLocaleString()}`
    ).join('\n');

    const subtotal = (selectedQuote.items || []).reduce((sum, item) => sum + calculateItemSubtotal(item), 0) || 0;
    const tax = (selectedQuote.items || []).reduce((sum, item) => sum + calculateItemTax(item), 0) || 0;

    const subject = `Quote ${selectedQuote.number} from Accountex`;
    const body = `Dear ${contact?.name || selectedQuote.contactName || 'Customer'},

I am providing you with a comprehensive explanation of Quote ${selectedQuote.number}, prepared on ${new Date(selectedQuote.date).toLocaleDateString()}.

This quote has been structured to cover all your requirements with the following details:

Line Items & Breakdown:
${itemsList}

Financial Overview:
- Subtotal: ${selectedQuote.currency} ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Tax Amount: ${selectedQuote.currency} ${tax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
- Quotation Value: ${selectedQuote.currency} ${selectedQuote.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

Terms:
This quote is valid until ${new Date(selectedQuote.expiryDate).toLocaleDateString()}.
${selectedQuote.termsAndConditions ? `\nAdditional Terms:\n${selectedQuote.termsAndConditions}` : ''}

We have carefully calculated these figures to ensure accuracy and value. Please let us know if you would like to proceed or if you require any adjustments.

Best regards,
Accountex Team`;
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipientEmails.join(','))}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(gmailUrl, '_blank');
  };

  const getLinkedInvoicesForQuote = (quote: Quote) => allInvoices.filter(invoice =>
    (invoice.quoteId === quote.id || invoice.sourceQuoteId === quote.id || invoice.id === quote.invoiceId) &&
    !['void', 'deleted', 'cancelled'].includes(String(invoice.status || '').toLowerCase())
  );

  const getLinkedDeliveriesForQuote = (quote: Quote) => {
    const invoiceIds = new Set(getLinkedInvoicesForQuote(quote).map(invoice => invoice.id));
    return allDeliveries.filter(delivery => {
      const invoiceId = delivery.invoiceId || delivery.metadata?.invoiceId;
      const sourceQuoteId = delivery.quoteId || delivery.sourceQuoteId || delivery.metadata?.quoteId || delivery.metadata?.sourceQuoteId;
      return sourceQuoteId === quote.id || invoiceIds.has(invoiceId);
    });
  };

  const getQuoteInvoiceStatus = (quote: Quote) => deriveInvoiceStatus((quote.items || []).map(item => ({
    quantity: Number(item.quantity || 0),
    completedQuantity: Number(item.invoicedQuantity || 0),
  })));

  const getQuoteDeliveryStatus = (quote: Quote) => {
    const deliveredByLine = new Map<string, number>();
    getLinkedDeliveriesForQuote(quote).forEach(delivery => {
      if (!['done', 'delivered'].includes(String(delivery.status || '').toLowerCase())) return;
      (delivery.items || []).forEach((item: any) => {
        const lineId = String(item.quoteLineId || item.id || item.itemId || item.itemCode || '');
        if (lineId) deliveredByLine.set(lineId, (deliveredByLine.get(lineId) || 0) + Number(item.quantityShipped || item.quantity || 0));
      });
    });
    return deriveDeliveryStatus((quote.items || []).map(item => ({
      quantity: Number(item.quantity || 0),
      completedQuantity: deliveredByLine.get(String(item.quoteLineId || item.id || '')) || 0,
    })));
  };

  const getQuoteRelatedDocuments = (quote: Quote): ChatterRelatedDocument[] => [
    ...getLinkedInvoicesForQuote(quote).map(invoice => ({
      id: invoice.id,
      label: invoice.number || (language === 'ar' ? 'فاتورة' : 'Invoice'),
      type: 'invoice',
      status: String(invoice.status || ''),
    })),
    ...getLinkedDeliveriesForQuote(quote).map(delivery => ({
      id: delivery.id,
      label: delivery.number || delivery.invoiceNumber || (language === 'ar' ? 'إشعار تسليم' : 'Delivery'),
      type: 'delivery',
      status: String(delivery.status || ''),
    })),
  ];

  const handleCreateInvoice = async (quote: Quote) => {
    const existingInvoice = getLinkedInvoicesForQuote(quote)[0];
    if (existingInvoice) {
      onNavigate?.('invoices', existingInvoice.id);
      return;
    }
    await handleUpdateStatus(quote.id, 'Invoiced');
  };

  const handleCreateDelivery = async (quote: Quote) => {
    const existingDelivery = getLinkedDeliveriesForQuote(quote)[0];
    if (existingDelivery) {
      onNavigate?.('deliveries', existingDelivery.id);
      return;
    }
    const trackedLines = (quote.items || []).filter(item => Boolean(item.trackInventory));
    if (!trackedLines.length) {
      notify.error(language === 'ar' ? 'لا توجد منتجات مخزنية لتسليمها.' : 'This order has no inventory-tracked products to deliver.');
      return;
    }
    notify.error(language === 'ar' ? 'لم يتم العثور على مسودة التسليم. احفظ الطلب مرة أخرى لإعادة إنشائها.' : 'The draft delivery is missing. Save the draft order again to recreate it.');
  };

  const filteredQuotes = quotes.filter(q => {
    const isDeletedFilterActive = filter === 'deleted' || statusFilter === 'Deleted';
    const stage = getQuoteStage(q);
    if (stage === 'Deleted' && !isDeletedFilterActive) return false;

    const matchesFilter = filter === 'all'
      ? q.type !== 'repeating_quote'
      : (filter === 'repeating'
          ? q.type === 'repeating_quote'
          : (q.type !== 'repeating_quote' && (filter === 'awaiting approval'
              ? stage === 'Sent'
              : (filter === 'awaiting payment'
                  ? stage === 'Accepted'
                  : stage.toLowerCase() === filter.toLowerCase()))));
    const contact = contacts.find(c => c.id === q.contactId);
    const contactName = contact?.name || 'Walk-in Customer';
    
    const tagMatchesSearch = q.tags?.some(tagName => tagName.toLowerCase().includes(searchQuery.toLowerCase())) || false;
    const matchesSearch = contactName.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         q.number.toLowerCase().includes(searchQuery.toLowerCase()) || tagMatchesSearch;
    
    // Advanced search
    const matchesRef = !refFilter || (q.reference || '').toLowerCase().includes(refFilter.toLowerCase()) || q.number.toLowerCase().includes(refFilter.toLowerCase());
    const matchesCustomer = !customerFilter || contactName.toLowerCase().includes(customerFilter.toLowerCase());
    const matchesTag = !tagFilter || (q.tags && q.tags.includes(tagFilter));
    const matchesStatus = statusFilter === 'all' || stage.toLowerCase() === statusFilter.toLowerCase();
    
    // Invoicing logic
    const invoicedAmount = getLinkedInvoicesForQuote(q)
      .reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const amountToInvoice = q.totalAmount - invoicedAmount;
    const invoiceStatus = invoicedAmount === 0 ? 'Not Invoiced' : (invoicedAmount >= q.totalAmount ? 'Fully Invoiced' : 'Partially Invoiced');

    const matchesInvoiceStatus = invoiceStatusFilter === 'all' || invoiceStatus.toLowerCase() === invoiceStatusFilter.toLowerCase();

    const matchesTotalAmount = (!minTotalAmount || q.totalAmount >= parseFloat(minTotalAmount)) && 
                              (!maxTotalAmount || q.totalAmount <= parseFloat(maxTotalAmount));
    
    const matchesInvoicedAmount = (!minInvoicedAmount || invoicedAmount >= parseFloat(minInvoicedAmount)) && 
                                 (!maxInvoicedAmount || invoicedAmount <= parseFloat(maxInvoicedAmount));

    const matchesToInvoiceAmount = (!minToInvoiceAmount || amountToInvoice >= parseFloat(minToInvoiceAmount)) && 
                                  (!maxToInvoiceAmount || amountToInvoice <= parseFloat(maxToInvoiceAmount));

    const matchesDateRange = (!startDate || q.date >= startDate) && 
                            (!endDate || q.date <= endDate);
                            
    return matchesFilter && matchesSearch && matchesDateRange && matchesRef && matchesCustomer && matchesTag && matchesStatus && matchesInvoiceStatus && matchesTotalAmount && matchesInvoicedAmount && matchesToInvoiceAmount;
  }).sort((a, b) => {
    if (sortDirection === 'asc') {
      return a.number.localeCompare(b.number);
    } else if (sortDirection === 'desc') {
      return b.number.localeCompare(a.number);
    }
    return 0;
  });


  const activeQuoteTheme = selectedQuote ? brandingThemes.find(t => t.id === selectedQuote.brandingTheme) : null;
  const selectedQuoteStage = selectedQuote ? getQuoteStage(selectedQuote) : 'Draft';

  const getFilterCount = (id: string) => {
    if (id === 'all') return null;
    return quotes.filter(q => {
      const stage = getQuoteStage(q);
      if (stage === 'Deleted' && id !== 'deleted') return false;

      if (id === 'repeating') return q.type === 'repeating_quote';
      if (q.type === 'repeating_quote') return false;
      if (id === 'awaiting approval') return stage === 'Sent';
      if (id === 'awaiting payment') return stage === 'Accepted';
      return stage.toLowerCase() === id.toLowerCase();
    }).length;
  };

  return (
    <div className="space-y-6 pt-2 sm:pt-4">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{language === 'ar' ? 'عروض الأسعار' : 'Quotes'}</h1>
          <p className="text-sm opacity-50 italic serif mt-1 text-foreground">{language === 'ar' ? 'إدارة وتتبع عروض أسعار العملاء' : 'Manage and track your customer quotations'}</p>
        </div>
        
        <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2 sm:pb-0 max-w-full">
          <button 
            onClick={() => {
              setIsRepeatingQuote(false);
              setIsEditing(false);
              let nextNum = orgSettings?.nextQuoteNumber || 1;
              const prefix = orgSettings?.quotePrefix || 'QT-';
              // Ensure uniqueness by checking existing quotes
              while (quotes.some(q => q.number === `${prefix}${nextNum}`)) {
                nextNum++;
              }
              setNextQuoteNumber(nextNum);
              setNewQuote(prev => ({
                ...prev,
                number: `${prefix}${nextNum}`,
                contactId: '',
                contactName: '',
                date: new Date().toISOString().split('T')[0],
                expiryDate: new Date(Date.now() + (orgSettings?.defaultPaymentTerms || 15) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                currency: normalizeCurrency(orgSettings?.defaultCurrency),
                items: [{ 
                  id: Math.random().toString(36).substring(2, 9),
                  item: '', 
                  description: '', 
                  quantity: 1, 
                  unitPrice: 0,
                  discount: 0,
                  discountType: 'percentage',
                  total: 0,
                  account: '',
                  taxRate: '0%',
                  trackingCategory: ''
                }],
                status: 'Draft',
                billingAddress: '',
                deliveryAddress: '',
                trackingCategory: ''
              }));
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:opacity-90 transition-all font-bold shadow-sm text-xs shrink-0 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>{language === 'ar' ? 'عرض سعر جديد' : 'New Quote'}</span>
          </button>

          <button 
            onClick={() => {
              setIsEditing(false);
              setIsRepeatingQuote(true);
              const startDate = new Date().toISOString().split('T')[0];
              const expiryDays = orgSettings?.defaultPaymentTerms || 7;
              const expiryDate = calculateQuoteExpiryDate(startDate, expiryDays);
              const prefix = orgSettings?.quotePrefix || 'QT-';
              let nextNum = orgSettings?.nextQuoteNumber || nextQuoteNumber || 1;
              while (quotes.some(q => q.number === `RPT-${prefix}${nextNum}`)) nextNum++;
              setRepeatingConfig({
                frequency: 1,
                frequencyUnit: 'Month',
                startDate,
                endDate: '',
                expiryDays,
                approveStatus: 'Draft',
                nextGenerationDate: startDate,
              });
              setNewQuote(prev => ({
                ...prev,
                number: `RPT-${prefix}${nextNum}`,
                contactId: '',
                contactName: '',
                contactEmail: '',
                date: startDate,
                expiryDate,
                reference: '',
                currency: normalizeCurrency(orgSettings?.defaultCurrency),
                items: [{
                  id: Math.random().toString(36).substring(2, 9),
                  item: '',
                  description: '',
                  quantity: 1,
                  unitPrice: 0,
                  discount: 0,
                  discountType: 'percentage',
                  total: 0,
                  account: '',
                  taxRate: '0%',
                  trackingCategory: ''
                }],
                totalAmount: 0,
                status: 'Draft',
                termsAndConditions: '',
                billingAddress: '',
                deliveryAddress: '',
                trackingCategory: '',
                type: 'repeating_quote',
                notes: '',
                tags: []
              }));
              setIsModalOpen(true);
            }}
            className="flex items-center gap-2 bg-white text-primary border border-primary/20 px-4 py-2 rounded-lg hover:bg-primary/5 transition-all font-bold shadow-sm text-xs shrink-0 whitespace-nowrap"
          >
            <RotateCcw className="w-4 h-4" />
            <span>{language === 'ar' ? 'تكرار' : 'Repeating'}</span>
          </button>

          <button 
            onClick={() => setIsRemindersModalOpen(true)}
            className="flex items-center gap-2 bg-white text-primary border border-primary/20 px-4 py-2 rounded-lg hover:bg-primary/5 transition-all font-bold shadow-sm text-xs shrink-0 whitespace-nowrap"
          >
            <Bell className="w-4 h-4" />
            <span>{language === 'ar' ? 'تذكير' : 'Reminders'}</span>
            {remindersSettings.enabled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-black text-green-700">
                <CheckCircle2 className="w-3 h-3" />
                <span>{language === 'ar' ? 'مفعل' : 'On'}</span>
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              announceDropdownOpen();
              setShowExportDropdown(false);
              setActiveAccountDropdown(null);
              setShowImportDropdown(true);
            }}
            className="flex items-center gap-2 bg-white text-primary border border-primary/20 px-4 py-2 rounded-lg hover:bg-primary/5 transition-all font-bold shadow-sm text-xs whitespace-nowrap"
          >
            <Upload className="w-4 h-4" />
            <span>{language === 'ar' ? 'استيراد' : 'Import'}</span>
          </button>

          <button
            type="button"
            onClick={() => {
              announceDropdownOpen();
              setShowImportDropdown(false);
              setActiveAccountDropdown(null);
              setShowExportDropdown(true);
            }}
            className="flex items-center gap-2 bg-white text-primary border border-primary/20 px-4 py-2 rounded-lg hover:bg-primary/5 transition-all font-bold shadow-sm text-xs whitespace-nowrap"
          >
            <FileText className="w-4 h-4" />
            <span>{language === 'ar' ? 'تصدير' : 'Export'}</span>
          </button>
        </div>
      </div>

      {(showImportDropdown || showExportDropdown) && (
        <div
          className="fixed inset-0 z-[180] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowImportDropdown(false);
              setShowExportDropdown(false);
            }
          }}
        >
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl border border-border bg-card text-foreground shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-export-dialog-title"
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  {language === 'ar' ? 'إدارة الملفات' : 'File management'}
                </p>
                <h2 id="import-export-dialog-title" className="mt-1 text-lg font-bold">
                  {showImportDropdown
                    ? (language === 'ar' ? 'استيراد عروض الأسعار' : 'Import quotes')
                    : (language === 'ar' ? 'تصدير عروض الأسعار' : 'Export quotes')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowImportDropdown(false);
                  setShowExportDropdown(false);
                }}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2 p-3">
              {showImportDropdown ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportDropdown(false);
                      handleImportCSV();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-primary transition-colors hover:bg-primary/5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <Upload className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block">{language === 'ar' ? 'رفع ملف CSV' : 'Upload CSV file'}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">{language === 'ar' ? 'استيراد عروض الأسعار من ملف' : 'Import quotes from a file'}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportDropdown(false);
                      handleDownloadCSVTemplate();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-primary transition-colors hover:bg-primary/5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <Download className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block">{language === 'ar' ? 'تحميل قالب CSV' : 'Download CSV template'}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">{language === 'ar' ? 'استخدمه لإعداد ملف الاستيراد' : 'Use it to prepare your import file'}</span>
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setShowExportDropdown(false);
                      handleExportExcel();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-primary transition-colors hover:bg-primary/5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <FileSpreadsheet className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block">{language === 'ar' ? 'إكسيل (.xlsx)' : 'Excel (.xlsx)'}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">{language === 'ar' ? 'تصدير البيانات إلى إكسيل' : 'Export data to Excel'}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowExportDropdown(false);
                      handleExportCSV();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-primary transition-colors hover:bg-primary/5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <FileText className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block">{language === 'ar' ? 'ملف CSV' : 'CSV file'}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">{language === 'ar' ? 'تصدير البيانات إلى CSV' : 'Export data to CSV'}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowExportDropdown(false);
                      if (selectedQuote) {
                        handleDownloadPDF();
                      } else {
                        notify.error('Select a quote to download as PDF');
                      }
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-bold text-primary transition-colors hover:bg-primary/5"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <FileText className="h-4 w-4 text-red-500" />
                    </span>
                    <span>
                      <span className="block">{language === 'ar' ? 'بي دي إف (.pdf)' : 'PDF (.pdf)'}</span>
                      <span className="mt-0.5 block text-xs font-medium text-muted-foreground">{language === 'ar' ? 'تنزيل عرض السعر المحدد' : 'Download the selected quote'}</span>
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-card border border-border p-4 rounded-2xl shadow-sm text-foreground">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {[
            { id: 'all', label: language === 'ar' ? 'الكل' : 'All' },
            { id: 'repeating', label: language === 'ar' ? 'متكرر' : 'Repeating' },
            { id: 'draft', label: language === 'ar' ? 'مسودة' : 'Draft' },
            { id: 'awaiting approval', label: language === 'ar' ? 'مُرسل' : 'Sent' },
            { id: 'declined', label: language === 'ar' ? 'مرفوض' : 'Declined' },
            { id: 'approved', label: language === 'ar' ? 'معتمد' : 'Approved' },
            { id: 'awaiting payment', label: language === 'ar' ? 'مقبول' : 'Accepted' },
            { id: 'invoiced', label: language === 'ar' ? 'مفوتر' : 'Invoiced' },
            { id: 'deleted', label: language === 'ar' ? 'محذوف' : 'Deleted' }
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 shrink-0",
                filter === f.id ? "bg-primary text-primary-foreground" : "hover:bg-primary/5 opacity-60 text-primary"
              )}
            >
              <span>{f.label}</span>
              {getFilterCount(f.id) !== null && (
                <span className={cn(
                  "px-1.5 py-0.5 rounded-full text-[10px]",
                  filter === f.id ? "bg-white text-primary" : "bg-primary/10 text-primary"
                )}>
                  {getFilterCount(f.id)}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-full border border-[#141414] text-sm font-bold transition-all",
                (startDate || endDate) ? "bg-primary text-primary-foreground" : "bg-white/50 hover:bg-primary/5 text-primary"
              )}
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </button>
            
            {showFilters && (
              <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white border border-[#141414] rounded-2xl p-6 shadow-2xl z-[60] text-primary">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-sm font-black uppercase tracking-widest">{language === 'ar' ? 'الفلاتر المتقدمة' : 'Advanced Filters'}</h3>
                  <button 
                    onClick={() => {
                      setStartDate('');
                      setEndDate('');
                      setRefFilter('');
                      setCustomerFilter('');
                      setTagFilter('');
                      setStatusFilter('all');
                      setInvoiceStatusFilter('all');
                      setMinTotalAmount('');
                      setMaxTotalAmount('');
                      setMinInvoicedAmount('');
                      setMaxInvoicedAmount('');
                      setMinToInvoiceAmount('');
                      setMaxToInvoiceAmount('');
                    }}
                    className="text-[10px] uppercase tracking-widest font-black opacity-30 hover:opacity-100 transition-opacity"
                  >
                    {language === 'ar' ? 'مسح الكل' : 'Clear All'}
                  </button>
                </div>

                <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                  {/* Date Filter */}
                  <div className="space-y-3">
                    <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'التاريخ' : 'Date Range'}</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[9px] uppercase font-bold opacity-30 italic">{language === 'ar' ? 'من' : 'From'}</label>
                        <input 
                          type="date" 
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[9px] uppercase font-bold opacity-30 italic">{language === 'ar' ? 'إلى' : 'To'}</label>
                        <input 
                          type="date" 
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reference & Customer */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'المرجع' : 'Reference'}</h4>
                      <input 
                        type="text" 
                        value={refFilter}
                        onChange={(e) => setRefFilter(e.target.value)}
                        placeholder="REF-001..."
                        className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'العميل' : 'Customer'}</h4>
                      <input 
                        type="text" 
                        value={customerFilter}
                        onChange={(e) => setCustomerFilter(e.target.value)}
                        placeholder="Customer name..."
                        className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                      />
                    </div>
                  </div>

                  {/* Statuses */}
                  <div className="grid grid-cols-2 gap-3">
                    <SearchableSelect 
                      label={language === 'ar' ? 'الحالة' : 'Status'}
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      options={[
                        { value: "all", label: language === 'ar' ? 'جميع الحالات' : 'All Statuses' },
                        { value: "Draft", label: language === 'ar' ? 'مسودة' : 'Draft' },
                        { value: "Sent", label: language === 'ar' ? 'مرسل' : 'Sent' },
                        { value: "Approved", label: language === 'ar' ? 'معتمد' : 'Approved' },
                        { value: "Accepted", label: language === 'ar' ? 'مقبول' : 'Accepted' },
                        { value: "Declined", label: language === 'ar' ? 'مرفوض' : 'Declined' },
                        { value: "Invoiced", label: language === 'ar' ? 'مفوتر' : 'Invoiced' },
                        { value: "Deleted", label: language === 'ar' ? 'محذوف' : 'Deleted' }
                      ]}
                    />

                    <SearchableSelect 
                      label={language === 'ar' ? 'حالة الفوترة' : 'Invoice Status'}
                      value={invoiceStatusFilter}
                      onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                      options={[
                        { value: "all", label: language === 'ar' ? 'جميع الفواتير' : 'All Invoicing' },
                        { value: "not invoiced", label: language === 'ar' ? 'غير مفوتر' : 'Not Invoiced' },
                        { value: "partially invoiced", label: language === 'ar' ? 'مفوتر جزئياً' : 'Partially Invoiced' },
                        { value: "fully invoiced", label: language === 'ar' ? 'مفوتر بالكامل' : 'Fully Invoiced' }
                      ]}
                    />
                  </div>

                  {/* Amounts */}
                  <div className="space-y-4 pt-2">
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'إجمالي المبلغ' : 'Total Amount'}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" value={minTotalAmount} onChange={e => setMinTotalAmount(e.target.value)} placeholder="Min" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                        <input type="number" value={maxTotalAmount} onChange={e => setMaxTotalAmount(e.target.value)} placeholder="Max" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'المبلغ المفوتر' : 'Invoiced Amount'}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" value={minInvoicedAmount} onChange={e => setMinInvoicedAmount(e.target.value)} placeholder="Min" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                        <input type="number" value={maxInvoicedAmount} onChange={e => setMaxInvoicedAmount(e.target.value)} placeholder="Max" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'المتبقي للفوترة' : 'Amount to Invoice'}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <input type="number" value={minToInvoiceAmount} onChange={e => setMinToInvoiceAmount(e.target.value)} placeholder="Min" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                        <input type="number" value={maxToInvoiceAmount} onChange={e => setMaxToInvoiceAmount(e.target.value)} placeholder="Max" className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none" />
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] uppercase tracking-widest font-bold opacity-40">{language === 'ar' ? 'الوسوم' : 'Tags'}</h4>
                    <input 
                      type="text" 
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      placeholder="Enter a tag..."
                      className="w-full bg-primary/[0.03] border border-primary/10 px-3 py-2 rounded-xl text-xs font-bold outline-none focus:ring-2 ring-primary/20"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <button 
                    onClick={() => setShowFilters(false)}
                    className="w-full bg-primary text-white py-3 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                  >
                    {language === 'ar' ? 'تطبيق الفلاتر' : 'Apply Filters'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 bg-white/50 border border-primary/20 px-4 py-2 rounded-full flex-1 md:w-64">
            <Search className="w-4 h-4 opacity-50" />
            <input 
              type="text" 
              placeholder="Search quotes..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm w-full"
            />
          </div>
          
          <div className="relative">
            <button 
              onClick={() => setShowColumnGear(!showColumnGear)}
              className="p-2 hover:bg-primary/5 rounded-full transition-all text-primary"
              title="Column selection"
            >
              <Settings className="w-5 h-5 opacity-60 hover:opacity-100" />
            </button>
            
            {showColumnGear && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl p-4 shadow-xl z-50">
                <h3 className="text-sm font-bold mb-3">Table Settings</h3>
                <div className="space-y-1 max-h-80 overflow-y-auto pr-1 custom-scrollbar">
                  <p className="text-[10px] uppercase font-bold opacity-40 mb-2">Visible & Order</p>
                  {columnOrder.map((colId, index) => (
                    <div 
                      key={colId}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('columnIndex', index.toString());
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromIndex = parseInt(e.dataTransfer.getData('columnIndex'));
                        const toIndex = index;
                        if (fromIndex === toIndex) return;
                        
                        const newOrder = [...columnOrder];
                        const [movedItem] = newOrder.splice(fromIndex, 1);
                        newOrder.splice(toIndex, 0, movedItem);
                        setColumnOrder(newOrder);
                      }}
                      className="flex items-center gap-2 hover:bg-primary/5 p-2 rounded-xl cursor-move group transition-colors border border-transparent hover:border-primary/10"
                    >
                      <div className="opacity-20 group-hover:opacity-100 transition-opacity">
                        <MoreHorizontal className="w-4 h-4 rotate-90" />
                      </div>
                      <input 
                        type="checkbox" 
                        checked={columnVisibility[colId]} 
                        onChange={(e) => {
                          e.stopPropagation();
                          setColumnVisibility(prev => ({ ...prev, [colId]: !prev[colId] }));
                        }}
                        className="rounded border-border accent-primary w-4 h-4 cursor-pointer"
                      />
                      <span className="text-xs font-bold text-primary capitalize flex-1 truncate">
                        {(() => {
                           if (colId === 'refNumber') return 'Ref Number';
                           if (colId === 'to') return 'Customer';
                           if (colId.startsWith('tag')) return `Tag ${colId.slice(3)}`;
                           return String(colId || '').replace(/([A-Z])/g, ' $1').trim();
                        })()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-3 border-t border-[#141414]/10">
                  <p className="text-[8px] text-center uppercase font-bold opacity-30 tracking-widest">Drag to reorder columns</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedQuotes.length > 0 && (
        <div className="flex items-center gap-4 bg-primary text-primary-foreground p-4 rounded-2xl mb-4 animate-in slide-in-from-top-2 duration-200 shadow-lg">
          <div className="flex items-center gap-2 pr-4 border-r border-primary-foreground/10">
            <span className="text-xs font-bold italic serif">{selectedQuotes.length} selected</span>
            <button 
              onClick={() => setSelectedQuotes([])}
              className="text-[10px] uppercase font-bold opacity-50 hover:opacity-100"
            >
              Clear
            </button>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-2 md:pb-0">
            {filter === 'draft' && (
              <button 
                onClick={() => handleBulkAction('submit')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <Send className="w-3 h-3" />
                <span>Submit for Approval</span>
              </button>
            )}
            {(filter === 'draft' || filter === 'awaiting approval') && (
              <button 
                onClick={() => handleBulkAction('approve')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <CheckCircle2 className="w-3 h-3" />
                <span>Approve</span>
              </button>
            )}
            {filter !== 'deleted' && (
              <button 
                onClick={() => handleBulkAction('delete')}
                className="flex items-center gap-2 px-4 py-2 hover:bg-red-500/20 text-red-400 rounded-full text-xs font-bold transition-all whitespace-nowrap"
              >
                <Trash2 className="w-3 h-3" />
                <span>Delete</span>
              </button>
            )}
            <button 
              onClick={() => handleBulkAction('print')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Printer className="w-3 h-3" />
              <span>Print</span>
            </button>
            <button 
              onClick={() => handleBulkAction('email')}
              className="flex items-center gap-2 px-4 py-2 hover:bg-white/10 rounded-full text-xs font-bold transition-all whitespace-nowrap"
            >
              <Mail className="w-3 h-3" />
              <span>Email</span>
            </button>
          </div>
        </div>
      )}

      {/* Quotes List */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xl text-foreground">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="p-4 w-10">
                  <input 
                    type="checkbox" 
                    className="rounded border-border accent-primary"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedQuotes(filteredQuotes.map(q => q.id));
                      } else {
                        setSelectedQuotes([]);
                      }
                    }}
                    checked={selectedQuotes.length === filteredQuotes.length && filteredQuotes.length > 0}
                  />
                </th>
                {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                  let label = '';
                  switch(colId) {
                    case 'refNumber': label = language === 'ar' ? 'رقم المرجع' : 'Ref Number'; break;
                    case 'discussion': label = ''; break;
                    case 'to': label = language === 'ar' ? 'العميل' : 'Customer'; break;
                    case 'issueDate': label = language === 'ar' ? 'التاريخ' : 'Date'; break;
                    case 'amountToInvoice': label = language === 'ar' ? 'المبلغ المتبقي للفوترة' : 'Amount to invoice'; break;
                    case 'amountInvoiced': label = language === 'ar' ? 'المبلغ المفوتر' : 'Amount invoiced'; break;
                    case 'totalAmount': label = language === 'ar' ? 'قيمة عرض السعر' : 'Quotation Value'; break;
                    case 'status': label = language === 'ar' ? 'الحالة' : 'Status'; break;
                    case 'invoiceStatus': label = language === 'ar' ? 'حالة الفوترة' : 'Invoice Status'; break;
                    case 'tag1': label = language === 'ar' ? 'وسم 1' : 'Tag 1'; break;
                    case 'tag2': label = language === 'ar' ? 'وسم 2' : 'Tag 2'; break;
                    case 'tag3': label = language === 'ar' ? 'وسم 3' : 'Tag 3'; break;
                    case 'tag4': label = language === 'ar' ? 'وسم 4' : 'Tag 4'; break;
                    case 'tag5': label = language === 'ar' ? 'وسم 5' : 'Tag 5'; break;
                  }
                  
                  return (
                    <th 
                      key={colId}
                      className={cn(
                        "p-4 text-xs font-bold uppercase tracking-wider opacity-50 italic serif text-foreground",
                        ['amountToInvoice', 'amountInvoiced', 'totalAmount'].includes(colId) && "text-right"
                      )}
                    >
                      {label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columnOrder.filter(id => columnVisibility[id]).length + 1} className="p-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-20" />
                  </td>
                </tr>
              ) : filteredQuotes.length === 0 ? (
                <tr>
                  <td colSpan={columnOrder.filter(id => columnVisibility[id]).length + 1} className="p-12 text-center opacity-50 italic serif text-primary">
                    No quotes found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredQuotes.map((quote) => {
                  const contact = contacts.find(c => c.id === quote.contactId);
                  
                  // Calculate billing metrics
                  const invoicesForQuote = getLinkedInvoicesForQuote(quote);
                  const amountInvoiced = invoicesForQuote.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
                  const totalQuoteAmount = quote.totalAmount || 0;
                  const amountToInvoice = Math.max(0, totalQuoteAmount - amountInvoiced);
                  
                  // Invoice status logic
                  let invStatus = { label: 'To Invoice', color: 'bg-blue-500/10 text-blue-600 border-blue-200' };
                  if (amountToInvoice <= 0.01) {
                    invStatus = { label: 'Fully Invoiced', color: 'bg-purple-500/10 text-purple-600 border-purple-200' };
                  } else if (amountInvoiced > 0) {
                    invStatus = { label: 'Partially Invoiced', color: 'bg-orange-500/10 text-orange-600 border-orange-200' };
                  }

                  return (
                    <tr 
                      key={quote.id} 
                      onClick={() => setSelectedQuote(quote)}
                      className="border-b border-primary/20 hover:bg-primary/5 transition-all group cursor-pointer"
                    >
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          className="rounded border-primary/30 accent-primary cursor-pointer w-4 h-4"
                          checked={selectedQuotes.includes(quote.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedQuotes(prev => [...prev, quote.id]);
                            } else {
                              setSelectedQuotes(prev => prev.filter(id => id !== quote.id));
                            }
                          }}
                        />
                      </td>
                      {columnOrder.filter(id => columnVisibility[id]).map(colId => {
                        switch(colId) {
                          case 'refNumber':
                            return (
                              <td key={colId} className="p-4 font-mono text-sm font-bold text-primary">
                                <div className="flex items-center gap-2">
                                  <span>{quote.number}</span>
                                  {quote.type === 'repeating_quote' && (
                                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] uppercase tracking-wider">Repeating</span>
                                  )}
                                </div>
                              </td>
                            );
                          case 'discussion':
                            return (
                              <td key={colId} className="p-4">
                                <div className="flex items-center justify-center">
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowThread(quote.id);
                                    }}
                                    className="p-1.5 hover:bg-primary/10 rounded-full transition-all text-primary"
                                    title="Open Thread"
                                  >
                                    <MessageSquare className="w-4 h-4" />
                                    {quote.threads && quote.threads.length > 0 && (
                                      <span className="text-xs font-bold text-red-500 ml-1">
                                        {quote.threads.length}
                                      </span>
                                    )}
                                  </button>
                                </div>
                              </td>
                            );
                          case 'to':
                            return (
                              <td key={colId} className="p-4" onClick={(e) => e.stopPropagation()}>
                                <button 
                                  onClick={() => {
                                    if (onNavigate && (quote.contactId)) {
                                      onNavigate('contacts', quote.contactId);
                                    }
                                  }}
                                  className="text-sm font-bold text-left hover:underline hover:text-primary transition-colors focus:outline-none"
                                >
                                  {(() => {
                                    const customerName = contact?.name || quote.contactName || 'Walk-in Customer';
                                    return typeof customerName === 'string' ? customerName : 'Walk-in Customer';
                                  })()}
                                </button>
                              </td>
                            );
                          case 'issueDate':
                            return <td key={colId} className="p-4 text-sm opacity-70">{new Date(quote.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>;
                          case 'amountToInvoice':
                            return (
                              <td key={colId} className="p-4 font-mono font-bold text-right text-orange-600">
                                {amountToInvoice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            );
                          case 'amountInvoiced':
                            return (
                              <td key={colId} className="p-4 font-mono font-bold text-right text-emerald-600">
                                {amountInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            );
                          case 'totalAmount':
                            return (
                              <td key={colId} className="p-4 font-mono font-bold text-right">
                                {totalQuoteAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            );
                          case 'status':
                            return (
                              <td key={colId} className="p-4">
                                {quote.type === 'repeating_quote' ? (
                                  <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
                                    <RotateCcw className="h-3.5 w-3.5" />
                                    {quote.repeatingConfig?.approveStatus || (language === 'ar' ? 'متكرر' : 'Repeating')}
                                  </span>
                                ) : <StatusBadge status={getQuoteStatusBadge(quote)} language={appLanguage} />}
                              </td>
                            );
                          case 'invoiceStatus':
                            return (
                              <td key={colId} className="p-4 font-bold text-right">
                                <span className={cn(
                                  "inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border",
                                  invStatus.color
                                )}>
                                  {invStatus.label}
                                </span>
                              </td>
                            );
                          case 'tag1':
                          case 'tag2':
                          case 'tag3':
                          case 'tag4':
                          case 'tag5': {
                            const tagIndex = parseInt(String(colId || '').replace('tag', '')) - 1;
                            const tagName = quote.tags?.[tagIndex];
                            if (!tagName) return <td key={colId} className="p-4"></td>;
                            
                            const tagDetails = availableTags.find((t: any) => t.name === tagName);
                            return (
                              <td key={colId} className="p-4">
                                <span 
                                  className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full text-[10px] font-bold border whitespace-nowrap"
                                  style={{
                                    backgroundColor: tagDetails ? `${tagDetails.color}15` : '#f3f4f6',
                                    color: tagDetails ? tagDetails.color : '#374151',
                                    borderColor: tagDetails ? `${tagDetails.color}30` : '#e5e7eb'
                                  }}
                                >
                                  <div className="p-0.5 rounded-full bg-white/50">
                                    <TagIcon className="w-2.5 h-2.5" />
                                  </div>
                                  {tagName}
                                </span>
                              </td>
                            );
                          }
                          default: return null;
                        }
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quote Details Modal */}
      {selectedQuote && (
        <div className="app-overlay-inset fixed inset-y-0 end-0 bg-[#E4E3E0] z-[100] flex flex-col overflow-hidden print-only print:inset-0">
          {/* Top Header / Breadcrumbs */}
          <div className="bg-background border-b border-primary/10 px-8 py-4 print:hidden text-primary">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-[11px] text-[#0073AA] font-bold">
                  <button onClick={() => setSelectedQuote(null)} className="hover:underline tracking-widest uppercase">Sales</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-[#141414]/40" />
                  <button onClick={() => setSelectedQuote(null)} className="hover:underline tracking-widest uppercase">Quotes</button>
                  <ChevronDown className="w-3 h-3 -rotate-90 text-[#141414]/40" />
                  <span className="tracking-widest uppercase text-[#141414]/60">{selectedQuote.number}</span>
                </div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-[#141414] tracking-tight italic serif">Quote {selectedQuote.number}</h2>
                  <StatusBadge status={getQuoteStatusBadge(selectedQuote)} language={appLanguage} className="uppercase tracking-widest" />
                  {getLinkedInvoicesForQuote(selectedQuote).length > 0 && (
                    <div className="flex items-center gap-1 ml-2">
                      <LinkIcon className="w-3 h-3 text-primary/40" />
                      <button
                        type="button"
                        onClick={() => setShowLinkedInvoices(true)}
                        className="text-xs font-bold text-[#0073AA] hover:underline flex items-center gap-1"
                        aria-haspopup="dialog"
                      >
                        View Linked Invoice{getLinkedInvoicesForQuote(selectedQuote).length > 1 ? 's' : ''}
                        <span className="bg-[#0073AA]/10 px-1.5 py-0.5 rounded-full text-[9px]">
                          {getLinkedInvoicesForQuote(selectedQuote).length}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {selectedQuoteStage === 'Draft' && (
                  <button 
                    onClick={() => handleUpdateStatus(selectedQuote.id, 'Sent')}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg font-bold text-[10px] hover:opacity-90 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                  >
                    <Send className="w-3 h-3" />
                    Send Quote
                  </button>
                )}
                {selectedQuoteStage === 'Sent' && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleUpdateStatus(selectedQuote.id, 'Accepted')}
                      className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-green-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                      Accept
                    </button>
                    <button 
                      onClick={() => requestDecline(selectedQuote)}
                      className="flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-red-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <XCircle className="w-3 h-3" />
                      Decline
                    </button>
                  </div>
                )}
                {selectedQuoteStage === 'Accepted' && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => void handleCreateDelivery(selectedQuote)}
                      className="flex items-center gap-2 border border-emerald-600 text-emerald-700 px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-emerald-50 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <Truck className="w-3 h-3" />
                      {language === 'ar' ? 'إنشاء تسليم' : 'Create Delivery'}
                    </button>
                    <button 
                      onClick={() => {
                        setPartialInvoiceItems((selectedQuote.items || []).map(item => ({
                          ...item,
                          qtyToInvoiceNow: 0
                        })));
                        setIsPartialInvoiceModalOpen(true);
                      }}
                      className="flex items-center gap-2 bg-purple-600 text-white px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-purple-700 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <Plus className="w-3 h-3" />
                      Create Partial Invoice
                    </button>
                    <button 
                      onClick={() => void handleCreateInvoice(selectedQuote)}
                      className="flex items-center gap-2 border border-purple-600 text-purple-600 px-3 py-1.5 rounded-lg font-bold text-[10px] hover:bg-purple-50 transition-all uppercase tracking-widest active:scale-95 shadow-sm"
                    >
                      <ArrowRight className="w-3 h-3" />
                      {language === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice'}
                    </button>
                  </div>
                )}
                <button 
                  onClick={handleEditQuote}
                  disabled={selectedQuoteStage === 'Accepted'}
                  className={cn(
                    "p-2 transition-colors",
                    selectedQuoteStage === 'Accepted'
                      ? "text-[#141414]/20 cursor-not-allowed"
                      : "text-[#141414]/40 hover:text-[#141414]"
                  )}
                  title={selectedQuoteStage === 'Accepted' ? 'Accepted quotes cannot be edited' : 'Edit Quote'}
                  aria-label={selectedQuoteStage === 'Accepted' ? 'Edit Quote (disabled for accepted quote)' : 'Edit Quote'}
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button className="text-[#141414]/40 hover:text-[#141414] transition-colors hover:scale-110 active:scale-95" onClick={() => handlePrint(selectedQuote)}>
                  <Printer className="w-5 h-5" />
                </button>
                <button 
                  onClick={handleDownloadPDF}
                  className="p-2 text-[#141414]/40 hover:text-[#141414] transition-colors"
                  title="Download PDF"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => setShowGetLinkModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-[#141414]/20 rounded-lg text-[10px] font-bold uppercase tracking-widest text-[#141414]/60 hover:text-[#141414] hover:border-[#141414] transition-all"
                  title="Share Live Link"
                >
                  <LinkIcon className="w-3.5 h-3.5" />
                  Get link
                </button>
                <button 
                  onClick={() => setShowQrModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-[#141414]/20 rounded-lg text-[10px] font-bold uppercase tracking-widest text-[#141414]/60 hover:text-[#141414] hover:border-[#141414] transition-all"
                  title="Scan with Mobile"
                >
                  <QrCode className="w-3.5 h-3.5" />
                  Scan QR
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShowCopyToMenu(prev => !prev)}
                    className="p-2 text-[#141414]/40 hover:text-[#141414] transition-colors"
                    aria-label="More quote options"
                    aria-expanded={showCopyToMenu}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                  {showCopyToMenu && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-[#141414]/15 rounded-2xl shadow-2xl z-[220] overflow-hidden">
                      <div className="px-4 py-3 border-b border-[#141414]/10 text-[10px] font-black uppercase tracking-widest text-[#141414]/45">Copy to</div>
                      {([
                        ['quote', 'Quote'], ['invoice', 'Invoice'], ['purchase_order', 'Purchase Order'], ['bill', 'Bill']
                      ] as const).map(([targetType, label]) => (
                        <button key={targetType} onClick={() => handleCopyQuote(targetType)} disabled={isCopying} className="w-full flex items-center gap-3 px-4 py-3 text-left text-xs font-bold text-[#141414] hover:bg-[#141414]/5 disabled:opacity-50 transition-colors">
                          {isCopying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4 opacity-50" />}
                          {label}
                        </button>
                      ))}
                      {selectedQuote.status !== 'Deleted' && (
                        <button
                          onClick={() => {
                            setShowCopyToMenu(false);
                            setQuoteToDelete(selectedQuote.id);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left text-xs font-bold text-red-600 hover:bg-red-50 border-t border-[#141414]/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          {language === 'ar' ? 'حذف عرض السعر' : 'Delete Quote'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button 
                  onClick={() => setSelectedQuote(null)}
                  className="ml-4 p-1 hover:bg-[#141414]/5 rounded-full transition-colors"
                >
                  <XCircle className="w-6 h-6 text-[#141414]/40 hover:text-[#141414]" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 md:p-16 custom-scrollbar text-[#141414] print:overflow-visible print:p-0 print:h-auto">
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Main Card */}
              <div className="bg-[#E4E3E0] border border-[#141414]/20 rounded-2xl shadow-xl overflow-hidden min-h-[600px]">
                {/* Metadata Grid */}
                <div className="p-8 grid grid-cols-1 md:grid-cols-6 gap-8 bg-white/30">
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Customer</p>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <p 
                          onClick={() => {
                            if (onNavigate && selectedQuote.contactId) {
                              onNavigate('customers', selectedQuote.contactId);
                            }
                          }}
                          className="text-sm font-bold text-[#141414] hover:underline cursor-pointer italic serif"
                        >
                          {contacts.find(c => c.id === selectedQuote.contactId)?.name || 'Walk-in Customer'}
                        </p>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendEmail();
                          }}
                          className="p-1 hover:bg-[#141414]/5 text-[#141414] rounded transition-colors group/mail"
                          title="Send Quote via Gmail"
                        >
                          <Mail className="w-3.5 h-3.5 group-hover/mail:scale-110 transition-transform" />
                        </button>
                      </div>
                      <div className="text-[10px] text-[#141414]/60 font-medium">
                        {(() => {
                          const contact = contacts.find(c => c.id === selectedQuote.contactId) || 
                                         contacts.find(c => c.name === selectedQuote.contactName);
                          const email = contact?.email || 
                                       (contact?.emails && contact.emails.length > 0 ? contact.emails[0] : null) || 
                                       (selectedQuote as any).contactEmail;
                          return email || 'No email provided';
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'التاريخ' : 'Date'}</p>
                    <p className="text-sm text-[#141414] font-bold">{new Date(selectedQuote.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry date'}</p>
                    <p className="text-sm text-[#141414] font-bold">{new Date(selectedQuote.expiryDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'المرجع' : 'Reference'}</p>
                    <p className="text-sm text-[#141414] font-bold">{selectedQuote.reference || '-'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Quote number</p>
                    <p className="text-sm text-[#141414] font-mono font-bold">{selectedQuote.number}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'العملة' : 'Currency'}</p>
                    <p className="text-sm text-[#141414] font-bold">{selectedQuote.currency}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Branding theme</p>
                    <p className="text-sm text-[#141414] font-bold">{activeQuoteTheme?.name || selectedQuote.brandingTheme || 'Standard'}</p>
                  </div>
                </div>

                <div className="px-8 py-4 text-right bg-white/10">
                  <p className="text-[10px] text-[#141414]/40 font-bold uppercase tracking-widest italic">Amounts are tax exclusive</p>
                </div>

                {/* Items Table */}
                <div className="border-t border-[#141414]/10">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-white/20 border-b border-[#141414]/10">
                        <th className="py-4 px-8 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Item (Code - Name)</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'الوصف' : 'Description'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Quantity</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">Unit Price</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">Tax rate</th>
                        {trackingCategories.map(cat => (
                          <th key={cat.id} className="py-4 px-4 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest">{cat.name}</th>
                        ))}
                        <th className="py-4 px-8 text-[11px] font-bold text-[#141414]/50 uppercase tracking-widest text-right">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#141414]/5">
                      {(selectedQuote.items || []).map((item, i) => {
                        const itemData = itemsList.find(it => it.code === item.item || it.id === item.item);
                        return (
                          <tr key={item.quoteLineId || item.id} className="hover:bg-white/40 transition-colors">
                            <td 
                              onClick={() => {
                                if (onNavigate && itemData?.id) {
                                  onNavigate('items', itemData.id);
                                }
                              }}
                              className="py-5 px-8 text-sm font-bold text-[#141414] hover:underline cursor-pointer"
                            >
                              {itemData?.code || item.item} {itemData?.name ? `- ${itemData.name}` : ''}
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] italic serif">{item.description}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{item.quantity}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{(item.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414] text-right">{(item.discount && item.discount > 0) ? ((item as any).discountType === 'amount' ? item.discount.toLocaleString() : `${item.discount}%`) : ''}</td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414]">
                              {item.account} {accountsList.find(acc => acc.code === item.account || acc.id === item.account)?.name ? `- ${accountsList.find(acc => acc.code === item.account || acc.id === item.account)?.name}` : ''}
                            </td>
                            <td className="py-5 px-4 text-sm font-bold text-[#141414]">{item.taxRate || 'Tax on Income'}</td>
                            {trackingCategories.map(cat => {
                              const trackingParts = (item.trackingCategory || '').split('|');
                              const valPart = trackingParts.find(p => p.startsWith(`${cat.id}:`));
                              const val = valPart ? valPart.split(':')[1] : '-';
                              return (
                                <td key={cat.id} className="py-5 px-4 text-sm font-bold text-[#141414]">
                                  {val}
                                </td>
                              );
                            })}
                            <td className="py-5 px-8 text-sm font-bold text-[#141414] text-right">
                              {calculateItemTotal(item).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Bottom Totals Section */}
                <div className="p-8 flex flex-col md:flex-row justify-between items-start gap-12 border-t border-[#141414]/10 bg-white/10">
                  <div className="space-y-6">
                    <div>
                      <input 
                        type="file" 
                        id="quote-file-upload" 
                        className="hidden" 
                        onChange={handleFileUpload}
                        disabled={isUploading}
                      />
                      <div className="flex flex-col gap-4">
                        <button 
                          onClick={() => document.getElementById('quote-file-upload')?.click()}
                          disabled={isUploading}
                          className="flex items-center gap-2 px-6 py-2.5 bg-[#141414] text-[#E4E3E0] rounded-xl text-xs font-bold uppercase tracking-widest hover:opacity-90 transition-all shadow-lg active:scale-95 disabled:opacity-50 relative overflow-hidden"
                        >
                          {isUploading && uploadProgress !== null && (
                            <div 
                              className="absolute inset-0 bg-white/10 transition-all duration-300 pointer-events-none"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          )}
                          <div className="flex items-center gap-2 relative z-10">
                            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                            <span>
                              {isUploading 
                                ? `Uploading ${uploadProgress ? Math.round(uploadProgress) : 0}%` 
                                : 'Attach files'
                              }
                            </span>
                            {!isUploading && <ChevronDown className="w-3 h-3 ml-1" />}
                          </div>
                        </button>

                        {/* Attachments List */}
                        {selectedQuote.attachments && selectedQuote.attachments.length > 0 && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            {selectedQuote.attachments.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between gap-3 p-3 bg-white/40 rounded-xl border border-primary/10 group hover:border-primary/30 transition-all text-primary">
                                <div className="flex items-center gap-3 overflow-hidden">
                                  <div className="p-2 bg-primary/5 rounded-lg">
                                    <FileText className="w-4 h-4 text-primary opacity-40" />
                                  </div>
                                  <div className="flex flex-col overflow-hidden">
                                    <span className="text-xs font-bold text-primary truncate">{file.name}</span>
                                    <span className="text-[10px] text-primary/40 font-mono uppercase">
                                      {(file.size / 1024).toFixed(1)} KB
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1">
                                  <a 
                                    href={file.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="p-1.5 hover:bg-primary/10 rounded-lg text-primary/40 hover:text-primary transition-colors"
                                    title="Download"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </a>
                                  <button 
                                    onClick={() => handleRemoveAttachment(file.url)}
                                    className="p-1.5 hover:bg-red-500/10 rounded-lg text-primary/40 hover:text-red-500 transition-colors"
                                    title="Remove"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {selectedQuote.termsAndConditions && (
                      <div className="max-w-md bg-white/20 p-4 rounded-xl border border-[#141414]/5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-[#141414]/40 mb-2">{language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}</p>
                        <p className="text-xs text-[#141414] leading-relaxed italic serif whitespace-pre-wrap">{selectedQuote.termsAndConditions}</p>
                      </div>
                    )}
                  </div>

                  <div className="w-full sm:w-fit min-w-[320px] max-w-full space-y-4 bg-primary/5 p-8 rounded-3xl border border-primary/10 shadow-inner">
                    <div className="flex justify-between items-center gap-8 text-xs font-bold text-primary/60 uppercase tracking-widest">
                      <span className="whitespace-nowrap">Total (Excl. Disc)</span>
                      <span className="text-primary whitespace-nowrap">{selectedQuote.currency} {selectedQuote.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {selectedQuote.items.reduce((sum, item) => {
                      const base = item.quantity * item.unitPrice;
                      const disc = item.discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                      return sum + disc;
                    }, 0) > 0 && (
                      <div className="flex justify-between items-center gap-8 text-xs font-bold text-primary/60 uppercase tracking-widest">
                        <span className="whitespace-nowrap">Less: Discount</span>
                        <span className="text-primary whitespace-nowrap">{selectedQuote.currency} {selectedQuote.items.reduce((sum, item) => {
                          const base = item.quantity * item.unitPrice;
                          const disc = item.discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                          return sum + disc;
                        }, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center gap-8 text-xs font-bold text-primary/60 uppercase tracking-widest border-t border-primary/10 pt-2">
                      <span className="whitespace-nowrap">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                      <span className="text-primary whitespace-nowrap">{selectedQuote.currency} {selectedQuote.items.reduce((sum, item) => sum + calculateItemSubtotal(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between items-center gap-8 text-xs font-bold text-primary/60 uppercase tracking-widest">
                      <span className="whitespace-nowrap">{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                      <span className="text-primary whitespace-nowrap">{selectedQuote.currency} {selectedQuote.items.reduce((sum, item) => sum + calculateItemTax(item), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="pt-6 mt-6 border-t border-primary/10 flex justify-between items-end gap-8">
                      <span className="text-xl font-bold uppercase tracking-tight text-primary italic serif whitespace-nowrap">{language === 'ar' ? 'قيمة عرض السعر' : 'Quotation Value'}</span>
                      <div className="text-right">
                        <span className="text-[10px] font-bold text-primary/40 block uppercase tracking-widest mb-1 whitespace-nowrap">Expires {new Date(selectedQuote.expiryDate).toLocaleDateString()}</span>
                        <span className="text-3xl font-bold text-primary tracking-tighter whitespace-nowrap">{selectedQuote.currency} {selectedQuote.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DocumentChatterPanel
                orgId={orgId}
                entityId={selectedQuote.id}
                entityType={language === 'ar' ? 'عرض سعر' : 'Quote'}
                locale={appLanguage}
                className="mb-12 lg:w-full lg:max-h-none"
                relatedDocuments={getQuoteRelatedDocuments(selectedQuote)}
                onRelatedDocumentOpen={related => {
                  setSelectedQuote(null);
                  onNavigate?.(related.type === 'delivery' ? 'deliveries' : 'invoices', related.id);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Linked Invoices Modal */}
      {showLinkedInvoices && selectedQuote && (
        <div
          className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShowLinkedInvoices(false);
          }}
        >
          <div
            className="bg-white rounded-[2rem] w-full max-w-xl overflow-hidden shadow-2xl border border-primary/10"
            role="dialog"
            aria-modal="true"
            aria-labelledby="linked-invoices-title"
          >
            <div className="p-8 border-b border-primary/5 flex items-center justify-between bg-primary/5">
              <div>
                <h3 id="linked-invoices-title" className="text-xl font-bold text-primary italic serif">Linked Invoices</h3>
                <p className="text-xs text-primary/40 font-bold uppercase tracking-widest mt-1">Invoices created from Quote {selectedQuote.number}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowLinkedInvoices(false)}
                className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all shadow-sm"
                aria-label="Close linked invoices"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-8 space-y-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
              {getLinkedInvoicesForQuote(selectedQuote).map(inv => (
                <button
                  type="button"
                  key={inv.id}
                  onClick={() => {
                    setShowLinkedInvoices(false);
                    setSelectedQuote(null);
                    onNavigate?.('invoices', inv.id);
                  }}
                  className="w-full flex items-center justify-between p-5 rounded-2xl border border-primary/5 hover:border-primary hover:bg-primary/5 transition-all text-left shadow-sm group active:scale-[0.99]"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <FileText className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-primary tracking-tight">#{inv.number}</p>
                      <p className="text-[10px] text-primary/50 font-bold uppercase tracking-widest">{inv.status}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <p className="text-sm font-bold text-primary tracking-tight">{formatQuoteCurrency(Number(inv.totalAmount || 0), inv.currency)}</p>
                    <div className="flex items-center gap-1 text-[9px] font-bold text-[#0073AA] uppercase tracking-widest">
                      View <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Quote Modal */}
      {isModalOpen && (
        <div className="app-overlay-inset ui-editor-screen fixed inset-y-0 end-0 bg-background z-[150] flex flex-col overflow-hidden text-primary">
          <div className="ui-editor-header flex items-center justify-between p-6 border-b border-primary/10 bg-primary/5">
            <h2 className="text-2xl font-bold italic serif tracking-tight">
              {isEditing ? (isRepeatingQuote ? 'Edit Repeating Quote' : 'Edit Quote') : (isRepeatingQuote ? 'Create Repeating Quote' : 'Create New Quote')}
            </h2>
            <button onClick={() => {
              setIsModalOpen(false);
              setIsEditing(false);
              setIsRepeatingQuote(false);
            }} className="p-2 hover:bg-primary/10 rounded-full transition-all text-primary">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
            <form onSubmit={handleAddQuote} className="ui-editor-form max-w-7xl mx-auto space-y-12">
              {isRepeatingQuote && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-white/50 p-6 rounded-2xl border border-primary/10">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                      {language === 'ar' ? 'كرر عرض السعر كل' : 'Repeat this quotation every'}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={repeatingConfig?.frequency || 1}
                        onChange={(e) => setRepeatingConfig(prev => ({ ...prev!, frequency: parseInt(e.target.value) || 1 }))}
                        className="w-20 bg-white border border-primary px-3 py-2 rounded-xl outline-none text-sm font-bold"
                      />
                      <SearchableSelect
                        value={repeatingConfig?.frequencyUnit || 'Month'}
                        onChange={(e) => setRepeatingConfig(prev => ({ ...prev!, frequencyUnit: e.target.value as 'Week' | 'Month' }))}
                        options={[
                          { value: 'Week', label: language === 'ar' ? 'أسبوع/أسابيع' : 'Week(s)' },
                          { value: 'Month', label: language === 'ar' ? 'شهر/شهور' : 'Month(s)' }
                        ]}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                      {language === 'ar' ? 'تاريخ عرض السعر' : 'Quotation Date'}
                    </label>
                    <input
                      type="date"
                      required
                      value={repeatingConfig?.startDate || ''}
                      onChange={(e) => {
                        const startDate = e.target.value;
                        const expiryDate = calculateQuoteExpiryDate(startDate, repeatingConfig?.expiryDays || 0);
                        setRepeatingConfig(prev => ({ ...prev!, startDate, nextGenerationDate: startDate }));
                        setNewQuote(prev => ({ ...prev, date: startDate, expiryDate }));
                      }}
                      className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                      {language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry Date'}
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{language === 'ar' ? 'تنتهي بعد' : 'Expires'}</span>
                      <input
                        type="number"
                        min="0"
                        value={repeatingConfig?.expiryDays || 0}
                        onChange={(e) => {
                          const expiryDays = Math.max(0, parseInt(e.target.value) || 0);
                          const startDate = repeatingConfig?.startDate || newQuote.date;
                          const expiryDate = calculateQuoteExpiryDate(startDate, expiryDays);
                          setRepeatingConfig(prev => ({ ...prev!, expiryDays }));
                          setNewQuote(prev => ({ ...prev, expiryDate }));
                        }}
                        className="w-16 bg-white border border-primary px-2 py-2 rounded-xl outline-none text-sm font-bold text-center"
                      />
                      <span className="text-sm">{language === 'ar' ? 'يوم/أيام بعد تاريخ العرض' : 'day(s) after quotation date'}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                      {language === 'ar' ? 'تاريخ التوقف (اختياري)' : 'End Date (Optional)'}
                    </label>
                    <input
                      type="date"
                      min={repeatingConfig?.startDate}
                      value={repeatingConfig?.endDate || ''}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev!, endDate: e.target.value }))}
                      className="w-full bg-white border border-primary px-4 py-3 rounded-xl outline-none text-sm font-bold"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-4">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-50">
                      {language === 'ar' ? 'حالة عروض الأسعار الناتجة' : 'Status for generated quotations'}
                    </label>
                    <SearchableSelect
                      value={repeatingConfig?.approveStatus || 'Draft'}
                      onChange={(e) => setRepeatingConfig(prev => ({ ...prev!, approveStatus: e.target.value as 'Draft' | 'Approved' | 'Sent' }))}
                      options={[
                        { value: 'Draft', label: language === 'ar' ? 'حفظ كمسودة' : 'Save as Draft' },
                        { value: 'Approved', label: language === 'ar' ? 'معتمد' : 'Approved' },
                        { value: 'Sent', label: language === 'ar' ? 'مرسل' : 'Sent' }
                      ]}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-primary">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-bold uppercase tracking-widest opacity-50">Contact *</label>
                      <button 
                        type="button"
                        onClick={() => setIsContactModalOpen(true)}
                        className="text-[10px] font-bold text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Add New
                      </button>
                    </div>
                    <SearchableSelect 
                      value={newQuote.contactId}
                      onChange={(e) => handleContactChange(e.target.value)}
                      placeholder={language === 'ar' ? 'اختر جهة اتصال' : 'Select a contact'}
                      options={contacts.map(c => ({ value: c.id, label: c.name }))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'المرجع' : 'Reference'}</label>
                    <input 
                      type="text" 
                      placeholder={language === 'ar' ? "مثل: مشروع ألفا" : "e.g. Project Alpha"}
                      value={newQuote.reference}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, reference: e.target.value }))}
                      className="w-full bg-white border border-primary/20 p-3 rounded-xl outline-none focus:border-primary transition-all text-primary font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'رقم عرض السعر' : 'Quote Number'}</label>
                    <input 
                      type="text" 
                      value={newQuote.number}
                      onChange={(e) => setNewQuote({ ...newQuote, number: e.target.value })}
                      className="w-full bg-white border border-primary/20 p-3 rounded-xl outline-none focus:border-primary transition-all text-primary font-bold"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'العملة' : 'Currency'}</label>
                        <button 
                          type="button"
                          onClick={() => setIsCurrencyModalOpen(true)}
                          className="text-[10px] font-bold text-primary hover:underline flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      </div>
                      <SearchableSelect 
                        value={newQuote.currency}
                        onChange={(e) => setNewQuote(prev => ({ ...prev, currency: e.target.value }))}
                        options={currencies.map(curr => ({ value: curr, label: curr }))}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'الحالة' : 'Status'}</label>
                      <SearchableSelect 
                        value={isRepeatingQuote ? repeatingConfig?.approveStatus || 'Draft' : newQuote.status}
                        disabled={isRepeatingQuote}
                        onChange={(e) => setNewQuote(prev => ({ ...prev, status: e.target.value as any }))}
                        options={isRepeatingQuote ? [
                          { value: repeatingConfig?.approveStatus || 'Draft', label: `Generated as ${repeatingConfig?.approveStatus || 'Draft'}` }
                        ] : [
                          { value: "Draft", label: "Draft" },
                          { value: "Sent", label: "Sent" },
                          { value: "Accepted", label: "Accepted" },
                          { value: "Declined", label: "Declined" },
                          { value: "Invoiced", label: "Invoiced" }
                        ]}
                      />
                    </div>

                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'التاريخ' : 'Date'}</label>
                    <input 
                      type="date" 
                      value={newQuote.date || ''}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry Date'}</label>
                    <input 
                      type="date" 
                      value={newQuote.expiryDate || ''}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, expiryDate: e.target.value }))}
                      className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'النسق' : 'Branding Theme'}</label>
                    <SearchableSelect 
                      value={newQuote.brandingTheme}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, brandingTheme: e.target.value }))}
                      className="p-3 text-sm bg-white/50 border-[#141414]"
                      options={[
                        { value: "Standard", label: language === 'ar' ? 'أساسي' : 'Standard' },
                        { value: "Professional", label: "Professional" },
                        { value: "Modern", label: "Modern" },
                        { value: "Minimal", label: "Minimal" },
                        ...brandingThemes.map(theme => ({ value: theme.id, label: theme.name }))
                      ]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest mb-2 opacity-50">{language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions'}</label>
                    <textarea 
                      rows={3}
                      placeholder="Enter terms and conditions..."
                      value={newQuote.termsAndConditions || ''}
                      onChange={(e) => setNewQuote(prev => ({ ...prev, termsAndConditions: e.target.value }))}
                      className="w-full bg-white/50 border border-[#141414] p-3 rounded-xl outline-none resize-none"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <label className="block text-xs font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'الوسوم' : 'Tags'}</label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <div key={idx} className="space-y-2">
                      <label className="block text-[10px] font-bold uppercase opacity-40">Tag {idx}</label>
                      <div className="relative group">
                        <SearchableSelect 
                          value={newQuote.tags?.[idx-1] || ''} 
                          onChange={(e) => {
                            const newTags = [...(newQuote.tags || [])];
                            // Ensure the array has enough space
                            while(newTags.length < idx) newTags.push('');
                            newTags[idx-1] = e.target.value;
                            setNewQuote(prev => ({ ...prev, tags: newTags }));
                          }}
                          className="p-2 text-xs bg-white/50 border-[#141414]"
                          options={[
                            { value: "", label: "None" },
                            ...availableTags.map(tag => ({ value: tag.name, label: tag.name }))
                          ]}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {availableTags.length === 0 && (
                  <p className="text-[10px] opacity-40 italic font-mono uppercase">No tags set up yet.</p>
                )}
              </div>

              {/* Items Table */}
              <div className="space-y-6">
                <h3 className="text-xl font-bold italic serif border-b border-[#141414] pb-2">{language === 'ar' ? 'عناصر الطلب' : 'Line Items'}</h3>
                <div className="overflow-x-auto pb-64 -mb-64">
                  <table className="w-full text-left border-collapse min-w-[1200px]">
                    <thead>
                      <tr className="border-b border-[#141414]">
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 border-r border-[#141414]/10">{language === 'ar' ? 'SKU / الصنف' : 'SKU / Item (optional)'}</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 border-r border-[#141414]/10">Description *</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 w-20 border-r border-[#141414]/10">QTY *</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 w-32 border-r border-[#141414]/10">Price *</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 w-20 border-r border-[#141414]/10">{language === 'ar' ? 'الخصم' : 'Disc'}</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 border-r border-[#141414]/10">{language === 'ar' ? 'الحساب' : 'Account'}</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 border-r border-[#141414]/10">{language === 'ar' ? 'معدل الضريبة' : 'Tax Rate'}</th>
                        {trackingCategories.map(cat => (
                          <th key={cat.id} className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 border-r border-[#141414]/10">{cat.name}</th>
                        ))}
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 text-right border-r border-[#141414]/10">{language === 'ar' ? 'المبلغ' : 'Amount'}</th>
                        <th className="py-2 px-3 text-[10px] font-bold uppercase opacity-50 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#141414]/10">
                      {(newQuote.items || []).map((item, index) => (
                        <tr key={index}>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <SearchableSelect 
                              value={item.item}
                              onChange={(e) => updateItem(index, 'item', e.target.value)}
                              className="bg-transparent border-none p-0 text-sm h-8"
                              placeholder={language === 'ar' ? 'اختر الصنف' : 'Select Item'}
                              options={itemsList.map((i) => ({ value: i.code, label: `SKU: ${i.code} - ${i.name}` }))}
                            />
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <input 
                              type="text" 
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                              className="w-full bg-transparent border-none outline-none text-sm"
                            />
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <input 
                              type="number" 
                              value={isNaN(item.quantity) ? '' : item.quantity}
                              onChange={(e) => {
                                const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                updateItem(index, 'quantity', isNaN(val) ? 0 : val);
                              }}
                              className="w-full bg-transparent border-none outline-none text-sm"
                            />
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <input 
                              type="number" 
                              value={isNaN(item.unitPrice) ? '' : item.unitPrice}
                              onChange={(e) => {
                                const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                updateItem(index, 'unitPrice', isNaN(val) ? 0 : val);
                              }}
                              className="w-full bg-transparent border-none outline-none text-sm"
                            />
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <div className="flex flex-col gap-1">
                              <input 
                                type="number" 
                                value={isNaN(item.discount || 0) ? '' : (item.discount || '')}
                                onChange={(e) => {
                                  const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                  updateItem(index, 'discount', isNaN(val) ? 0 : val);
                                }}
                                className="w-full bg-transparent border-none outline-none text-sm"
                              />
                              <SearchableSelect
                                value={item.discountType || 'percentage'}
                                onChange={(e) => updateItem(index, 'discountType', e.target.value)}
                                className="w-full bg-transparent text-[9px] uppercase font-bold opacity-40 outline-none cursor-pointer"
                                options={[
                                  { value: "percentage", label: "%" },
                                  { value: "amount", label: "Amt" }
                                ]}
                              />
                            </div>
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10 relative">
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  if (activeAccountDropdown === index) {
                                    setActiveAccountDropdown(null);
                                    return;
                                  }
                                  announceDropdownOpen();
                                  setShowImportDropdown(false);
                                  setShowExportDropdown(false);
                                  setActiveAccountDropdown(index);
                                }}
                                className="w-full flex items-center justify-between text-left text-sm hover:bg-black/5 p-1 rounded transition-colors"
                              >
                                <span className="truncate font-mono text-xs">
                                  {item.account ? (
                                    (() => {
                                      const acc = accountsList.find(a => a.code === item.account);
                                      return acc ? `${acc.code} - ${acc.name}` : item.account;
                                    })()
                                  ) : (language === 'ar' ? 'اختر الحساب' : 'Select Account')}
                                </span>
                                <ChevronDown className="w-3 h-3 opacity-50" />
                              </button>

                              {activeAccountDropdown === index && (
                                <div className="absolute top-full left-0 w-80 bg-white border border-[#141414] shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-xl z-[200] mt-1 overflow-hidden">
                                  <div className="p-3 border-b border-[#141414]/10 bg-[#141414]/5">
                                    <div className="relative">
                                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40" />
                                      <input
                                        autoFocus
                                        type="text"
                                        placeholder={language === 'ar' ? 'البحث بالكود أو الاسم...' : 'Search by code or name...'}
                                        value={accountSearchQuery}
                                        onChange={(e) => setAccountSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#141414]/20 rounded-lg focus:outline-none focus:border-[#141414] shadow-inner"
                                      />
                                    </div>
                                  </div>
                                  <div className="max-h-64 overflow-y-auto custom-scrollbar bg-white">
                                    {accountsList
                                      .filter(acc => 
                                        acc.code.toLowerCase().includes(accountSearchQuery.toLowerCase()) ||
                                        acc.name.toLowerCase().includes(accountSearchQuery.toLowerCase())
                                      ).length > 0 ? (
                                      accountsList
                                        .filter(acc => 
                                          acc.code.toLowerCase().includes(accountSearchQuery.toLowerCase()) ||
                                          acc.name.toLowerCase().includes(accountSearchQuery.toLowerCase())
                                        )
                                        .map((acc) => (
                                          <button
                                            key={acc.id}
                                            type="button"
                                            onClick={() => {
                                              updateItem(index, 'account', acc.code);
                                              setActiveAccountDropdown(null);
                                              setAccountSearchQuery('');
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-[#141414] hover:text-[#E4E3E0] transition-colors flex flex-col border-b border-[#141414]/5 last:border-0 group"
                                          >
                                            <span className="font-bold text-sm group-hover:text-white">{acc.code}</span>
                                            <span className="opacity-60 text-xs group-hover:text-white/80">{acc.name}</span>
                                          </button>
                                        ))
                                    ) : (
                                      <div className="px-4 py-8 text-center opacity-40 text-sm italic">
                                        {language === 'ar' ? 'لم يتم العثور على حسابات' : 'No accounts found'}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setIsAccountModalOpen(true);
                                    }}
                                    className="w-full text-left px-4 py-4 text-sm text-primary hover:bg-primary/10 transition-colors font-bold flex items-center gap-2 border-t border-primary/10 sticky bottom-0 bg-white z-10"
                                  >
                                    <Plus className="w-4 h-4" />
                                    {language === 'ar' ? 'إضافة حساب جديد' : 'Add New Account'}
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-3 border-r border-[#141414]/10">
                            <SearchableSelect 
                              value={item.taxRate}
                              onChange={(e) => updateItem(index, 'taxRate', e.target.value)}
                              className="bg-transparent border-none p-0 text-sm h-8"
                              options={[
                                { value: "0%", label: "Tax Exempt (0%)" },
                                ...taxRates.filter(t => t.type !== 'purchases').map(tax => ({ value: tax.name, label: `${tax.name} (${tax.rate}%)` }))
                              ]}
                            />
                          </td>
                          {trackingCategories.map(cat => (
                            <td key={cat.id} className="py-2 px-3 border-r border-[#141414]/10">
                              <SearchableSelect 
                                value={item.trackingCategory?.split('|').find(p => p.startsWith(`${cat.id}:`))?.split(':')[1] || ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const currentParts = item.trackingCategory ? item.trackingCategory.split('|') : [];
                                  const otherParts = currentParts.filter(p => !p.startsWith(`${cat.id}:`));
                                  const newParts = val ? [...otherParts, `${cat.id}:${val}`] : otherParts;
                                  updateItem(index, 'trackingCategory', newParts.join('|'));
                                }}
                                className="w-full bg-transparent border-none outline-none text-sm"
                                options={[
                                  { value: "", label: language === 'ar' ? 'لا يوجد' : 'None' },
                                  ...cat.options.map((opt: string) => ({ value: opt, label: opt }))
                                ]}
                              />
                            </td>
                          ))}
                          <td className="py-2 px-3 text-right font-mono font-bold text-sm border-r border-[#141414]/10">
                            {formatQuoteCurrency(item.total || 0, newQuote.currency || orgSettings?.defaultCurrency)}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <button 
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-1.5 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                              title={language === 'ar' ? 'إزالة الصنف' : 'Remove item'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button 
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-2 text-sm font-bold text-primary hover:underline transition-all mt-4"
                >
                  <Plus className="w-4 h-4" />
                  {language === 'ar' ? 'إضافة صف' : 'Add row'}
                </button>
              </div>

              <div className="flex flex-col items-end gap-2 pt-12 border-t border-[#141414] w-full max-w-md ml-auto">
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold">{language === 'ar' ? 'الإجمالي (قبل الخصم)' : 'Total (Excl. Disc)'}</span>
                  <span className="font-bold font-mono">
                    {formatQuoteCurrency(newQuote.items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0), newQuote.currency || orgSettings?.defaultCurrency)}
                  </span>
                </div>
                {newQuote.items.reduce((sum, item) => {
                  const base = item.quantity * item.unitPrice;
                  const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                  return sum + disc;
                }, 0) > 0 && (
                  <div className="flex justify-between w-full text-sm">
                    <span className="opacity-50 uppercase font-bold text-red-500">{language === 'ar' ? 'ناقص: الخصم' : 'Less: Discount'}</span>
                    <span className="font-bold font-mono text-red-500">
                      -{formatQuoteCurrency(newQuote.items.reduce((sum, item) => {
                        const base = item.quantity * item.unitPrice;
                        const disc = (item as any).discountType === 'amount' ? (item.discount || 0) : (base * (item.discount || 0) / 100);
                        return sum + disc;
                      }, 0), newQuote.currency || orgSettings?.defaultCurrency)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between w-full text-sm border-t border-[#141414]/10 pt-2">
                  <span className="opacity-50 uppercase font-bold">{language === 'ar' ? 'المجموع الفرعي' : 'Subtotal'}</span>
                  <span className="font-bold font-mono">
                    {formatQuoteCurrency(newQuote.items.reduce((sum, item) => sum + calculateItemSubtotal(item), 0), newQuote.currency || orgSettings?.defaultCurrency)}
                  </span>
                </div>
                <div className="flex justify-between w-full text-sm">
                  <span className="opacity-50 uppercase font-bold">{language === 'ar' ? 'إجمالي الضريبة' : 'Total Tax'}</span>
                  <span className="font-bold font-mono">
                    {formatQuoteCurrency(newQuote.items.reduce((sum, item) => sum + calculateItemTax(item), 0), newQuote.currency || orgSettings?.defaultCurrency)}
                  </span>
                </div>
                <div className="w-full border-t border-[#141414] my-2 opacity-20"></div>
                <div className="text-right w-full">
                  <p className="text-xs opacity-50 uppercase font-bold text-primary italic serif">{language === 'ar' ? 'قيمة عرض السعر' : 'Quotation Value'}</p>
                  <p className="text-2xl font-bold font-mono tracking-tight text-primary">
                    {formatQuoteCurrency(newQuote.items.reduce((sum, item) => sum + calculateItemTotal(item), 0), newQuote.currency || orgSettings?.defaultCurrency)}
                  </p>
                </div>
                <div className="ui-editor-actions">
                  <button 
                    type="button"
                    data-editor-secondary="true"
                    onClick={() => {
                      setIsModalOpen(false);
                      setIsEditing(false);
                      setIsRepeatingQuote(false);
                    }}
                    className="px-12 py-4 rounded-full border border-primary/20 font-bold hover:bg-primary/10 transition-all text-primary"
                  >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                  <button 
                    type="submit"
                    data-editor-primary="true"
                    disabled={isSaving}
                    className={cn(
                      "px-20 py-4 rounded-full bg-primary text-primary-foreground font-bold transition-all flex items-center gap-3",
                      isSaving ? "opacity-50 cursor-not-allowed" : "hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] cursor-pointer shadow-lg"
                    )}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin text-primary-foreground" />
                        <span>{language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}</span>
                      </>
                    ) : (
                      <span>{isRepeatingQuote ? 'Save Repeating Quote' : (language === 'ar' ? 'حفظ عرض السعر' : 'Save Quote')}</span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold italic serif">{language === 'ar' ? 'إضافة جهة اتصال جديدة' : 'Add New Contact'}</h2>
              <button onClick={() => setIsContactModalOpen(false)} className="p-1 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddContact} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1 opacity-50">{language === 'ar' ? 'الاسم *' : 'Name *'}</label>
                <input 
                  type="text" 
                  required
                  value={newContact.name}
                  onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-2 rounded-lg text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1 opacity-50">{language === 'ar' ? 'البريد الإلكتروني (اختياري)' : 'Email (optional)'}</label>
                <input 
                  type="email" 
                  value={newContact.email}
                  onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-white/50 border border-[#141414] p-2 rounded-lg text-sm outline-none"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsContactModalOpen(false)}
                  className="flex-1 py-2 rounded-full border border-[#141414] text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  type="submit"
                  className="flex-1 py-2 rounded-full bg-[#141414] text-[#E4E3E0] text-xs font-bold hover:opacity-90 transition-all"
                >
                  {language === 'ar' ? 'حفظ جهة الاتصال' : 'Save Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Currency Modal */}
      {isCurrencyModalOpen && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-xs rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold italic serif">{language === 'ar' ? 'إضافة عملة' : 'Add Currency'}</h2>
              <button onClick={() => setIsCurrencyModalOpen(false)} className="p-1 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddCurrency} className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold uppercase mb-1 opacity-50">{language === 'ar' ? 'كود العملة' : 'Currency Code'}</label>
                <input 
                  type="text" 
                  required
                  placeholder={language === 'ar' ? 'مثال: JPY' : 'e.g. JPY'}
                  maxLength={3}
                  value={customCurrency}
                  onChange={(e) => setCustomCurrency(e.target.value)}
                  className="w-full bg-white/50 border border-[#141414] p-2 rounded-lg text-sm outline-none uppercase"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsCurrencyModalOpen(false)}
                  className="flex-1 py-2 rounded-full border border-[#141414] text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button 
                  type="submit"
                  className="flex-1 py-2 rounded-full bg-[#141414] text-[#E4E3E0] text-xs font-bold hover:opacity-90 transition-all"
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Discussion Thread Modal */}
      {showThread && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-3xl shadow-2xl h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-8 border-b border-[#141414]/10 bg-white/30">
               <div className="flex items-center gap-4">
                 <div className="p-3 bg-[#141414] text-[#E4E3E0] rounded-2xl">
                    <MessageSquare className="w-6 h-6" />
                 </div>
                 <div className="flex flex-col">
                   <h2 className="text-2xl font-bold italic serif tracking-tight leading-none">{language === 'ar' ? 'المناقشة' : 'Discussion'}</h2>
                   <p className="text-[10px] font-bold opacity-30 uppercase tracking-widest mt-1">Internal thread for quote {showThread}</p>
                 </div>
               </div>
               <button 
                 onClick={() => setShowThread(null)} 
                 className="p-3 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all hover:rotate-90"
               >
                 <XCircle className="w-6 h-6" />
               </button>
            </div>
            <div className="flex-1 overflow-hidden p-0 relative">
              <DiscussionsView 
                orgId={orgId} 
                documentId={showThread} 
                collectionName="documents" 
                language={language}
                onClose={() => setShowThread(null)}
              />
            </div>
          </div>
        </div>
      )}
      
      <CancelDocumentDialog
        open={Boolean(quoteToDecline)}
        onOpenChange={open => { if (!open) setQuoteToDecline(null); }}
        onConfirm={handleConfirmDecline}
        pending={isDeclineSaving}
        documentLabel={quoteToDecline?.number}
        language={appLanguage}
      />

      {/* Delete Confirmation Modal */}
      {quoteToDelete && (
        <div className="fixed inset-0 bg-[#141414]/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-[#E4E3E0] border-2 border-[#141414] rounded-[2rem] p-8 max-w-md w-full shadow-[16px_16px_0px_0px_rgba(20,20,20,1)] animate-in zoom-in duration-300">
            <div className="flex items-center gap-4 mb-6 text-red-600">
              <div className="p-3 bg-red-100 rounded-2xl">
                <XCircle className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold italic serif">{language === 'ar' ? 'تأكيد الحذف' : 'Confirm Deletion'}</h3>
            </div>
            
            <p className="text-sm font-medium mb-8 leading-relaxed text-[#141414]">
              {language === 'ar'
                ? 'هل أنت متأكد أنك تريد حذف عرض السعر؟ ستتغير حالته إلى محذوف ويمكنك العثور عليه لاحقاً باستخدام فلتر المحذوف.'
                : 'Are you sure you want to delete this quote? Its status will change to Deleted, and you can find it later using the Deleted filter.'}
            </p>
            
            <div className="flex gap-4">
              <button 
                onClick={() => setQuoteToDelete(null)}
                className="flex-1 py-3 rounded-full border-2 border-[#141414] text-xs font-bold hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
              >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
              <button 
                onClick={() => {
                  if (quoteToDelete) {
                    handleDeleteQuote(quoteToDelete);
                    setQuoteToDelete(null);
                  }
                }}
                className="flex-1 py-3 rounded-full bg-red-600 text-white text-xs font-bold hover:bg-red-700 transition-all shadow-[4px_4px_0px_0px_rgba(20,20,20,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
              >
                {language === 'ar' ? 'حذف عرض السعر' : 'Delete Quote'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Partial Invoicing Modal */}
      {isPartialInvoiceModalOpen && selectedQuote && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] w-full max-w-4xl rounded-[32px] border border-[#141414] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-[#141414]/10 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold italic serif">{language === 'ar' ? 'إنشاء فاتورة جزئية' : 'Create Partial Invoice'}</h3>
                <p className="text-sm opacity-50">{language === 'ar' ? `اختر الكميات المطلوب فوترتها لعرض السعر ${selectedQuote.number}` : `Select quantities to invoice for Quote ${selectedQuote.number}`}</p>
              </div>
              <button 
                onClick={() => setIsPartialInvoiceModalOpen(false)} 
                className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#141414]/10">
                    <th className="py-4 text-[10px] font-bold uppercase tracking-widest opacity-50">{language === 'ar' ? 'اسم المنتج' : 'Product Name'}</th>
                    <th className="py-4 text-[10px] font-bold uppercase tracking-widest opacity-50 text-center">{language === 'ar' ? 'الكمية الأصلية' : 'Original Qty'}</th>
                    <th className="py-4 text-[10px] font-bold uppercase tracking-widest opacity-50 text-center">{language === 'ar' ? 'تمت فوترتها سابقاً' : 'Previously Invoiced'}</th>
                    <th className="py-4 text-[10px] font-bold uppercase tracking-widest opacity-50 text-right w-40">{language === 'ar' ? 'الكمية المراد فوترتها الآن' : 'Qty to Invoice Now'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#141414]/5">
                  {partialInvoiceItems.map((item, index) => {
                    const remaining = item.quantity - (item.invoicedQuantity || 0);
                    return (
                      <tr key={index} className="group">
                        <td className="py-4">
                          <p className="font-bold text-sm">{item.item || 'Item'}</p>
                          <p className="text-xs opacity-50 italic serif">{item.description}</p>
                        </td>
                        <td className="py-4 text-center text-sm font-mono">{item.quantity}</td>
                        <td className="py-4 text-center text-sm font-mono opacity-50">{item.invoicedQuantity || 0}</td>
                        <td className="py-4 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <input 
                              type="number"
                              min="0"
                              max={remaining}
                              value={item.qtyToInvoiceNow}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                const newItems = [...partialInvoiceItems];
                                newItems[index].qtyToInvoiceNow = val;
                                setPartialInvoiceItems(newItems);
                              }}
                              className={cn(
                                "w-24 bg-white border border-[#141414] px-3 py-2 rounded-xl text-right font-mono text-sm outline-none focus:ring-2 focus:ring-[#141414]/20 transition-all",
                                item.qtyToInvoiceNow > remaining && "border-red-500 bg-red-50",
                                remaining === 0 && "opacity-50 cursor-not-allowed bg-primary/10"
                              )}
                              disabled={remaining === 0}
                            />
                            {remaining > 0 && (
                              <button 
                                onClick={() => {
                                  const newItems = [...partialInvoiceItems];
                                  newItems[index].qtyToInvoiceNow = remaining;
                                  setPartialInvoiceItems(newItems);
                                }}
                                className="text-[9px] font-bold uppercase text-[#0073AA] hover:underline"
                              >
                                Invoice Remaining ({remaining})
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-8 border-t border-[#141414]/10 bg-white/30 flex items-center justify-between">
              <div className="text-sm italic serif opacity-50">
                {language === 'ar' ? 'ملاحظة: العناصر المفوترة ستظهر كفاتورة مسودة جديدة.' : 'Note: Invoiced items will appear as a new Draft Invoice.'}
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setIsPartialInvoiceModalOpen(false)}
                  className="px-8 py-3 rounded-full border border-[#141414] font-bold text-xs uppercase tracking-widest hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  onClick={handleConfirmPartialInvoice}
                  disabled={isPartialSaving}
                  className="flex items-center gap-2 px-8 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-50 shadow-xl active:scale-95"
                >
                  {isPartialSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" />
                  )}
                  <span>{language === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* New Account Modal */}
      {isAccountModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] w-full max-w-md rounded-3xl border border-[#141414] shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between">
              <h3 className="text-xl font-bold italic serif">{language === 'ar' ? 'إضافة حساب جديد' : 'Add New Account'}</h3>
              <button onClick={() => setIsAccountModalOpen(false)} className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddAccount} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase opacity-50">{language === 'ar' ? 'الكود *' : 'Code *'}</label>
                  <input
                    required
                    type="text"
                    value={newAccount.code}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full bg-white border border-[#141414] px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#141414]/20"
                    placeholder="e.g. 4000"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase opacity-50">{language === 'ar' ? 'النوع *' : 'Type *'}</label>
                  <select
                    required
                    value={newAccount.type}
                    onChange={(e) => setNewAccount(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full bg-white border border-[#141414] px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#141414]/20"
                  >
                    <option value="Revenue">{language === 'ar' ? 'إيرادات' : 'Revenue'}</option>
                    <option value="Sales">{language === 'ar' ? 'مبيعات' : 'Sales'}</option>
                    <option value="Other Income">{language === 'ar' ? 'دخل آخر' : 'Other Income'}</option>
                    <option value="Expense">{language === 'ar' ? 'مصروفات' : 'Expense'}</option>
                    <option value="Direct Costs">{language === 'ar' ? 'تكاليف مباشرة' : 'Direct Costs'}</option>
                    <option value="Overhead">{language === 'ar' ? 'تكاليف عامة' : 'Overhead'}</option>
                    <option value="Asset">{language === 'ar' ? 'أصل' : 'Asset'}</option>
                    <option value="Liability">{language === 'ar' ? 'التزام' : 'Liability'}</option>
                    <option value="Equity">{language === 'ar' ? 'حقوق ملكية' : 'Equity'}</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase opacity-50">{language === 'ar' ? 'الاسم *' : 'Name *'}</label>
                <input
                  required
                  type="text"
                  value={newAccount.name}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-white border border-[#141414] px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#141414]/20"
                  placeholder="e.g. Sales Revenue"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase opacity-50">{language === 'ar' ? 'الوصف' : 'Description'}</label>
                <textarea
                  value={newAccount.description}
                  onChange={(e) => setNewAccount(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-white border border-[#141414] px-4 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#141414]/20 min-h-[80px]"
                  placeholder="Account description..."
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsAccountModalOpen(false)}
                  className="flex-1 px-6 py-3 rounded-full border border-[#141414] font-bold text-sm hover:bg-[#141414] hover:text-[#E4E3E0] transition-all"
                >{language === 'ar' ? 'إلغاء' : 'Cancel'}</button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 rounded-full bg-[#141414] text-[#E4E3E0] font-bold text-sm hover:opacity-90 transition-all shadow-[4px_4px_0px_0px_rgba(20,20,20,0.2)]"
                >
                  {language === 'ar' ? 'إضافة الحساب' : 'Add Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Get Link Modal */}
      {showGetLinkModal && selectedQuote && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-[#141414]/20 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest italic serif">Get link</h3>
              <button 
                onClick={() => setShowGetLinkModal(false)}
                className="p-1 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-[#141414]/40" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <p className="text-xs font-bold text-[#141414]/60 leading-relaxed">
                Anyone with this link will be able to view this quotation live through the client portal.
              </p>
              
              <div className="relative group">
                <input 
                  type="text" 
                  readOnly 
                  value={getPortalUrl('quote', selectedQuote.id)}
                  className="w-full bg-white/50 border border-[#141414]/10 rounded-xl px-4 py-3 text-[11px] font-mono pr-20 outline-none focus:border-[#141414]/30 transition-all font-bold"
                />
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(getPortalUrl('quote', selectedQuote.id));
                    notify.confirm('Link copied to clipboard');
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 bg-[#141414] text-[#E4E3E0] rounded-lg text-[10px] font-bold uppercase tracking-widest hover:opacity-90 transition-all active:scale-95"
                >
                  Copy
                </button>
              </div>

              <div className="pt-4">
                <button 
                  onClick={() => setShowGetLinkModal(false)}
                  className="w-full py-4 text-[#141414] font-bold text-xs uppercase tracking-widest hover:bg-[#141414]/5 rounded-xl transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && selectedQuote && (
        <div className="fixed inset-0 bg-[#141414]/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl border border-[#141414]/20 animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-[#141414]/10 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-widest italic serif">Scan QR Code</h3>
              <button 
                onClick={() => setShowQrModal(false)}
                className="p-1 hover:bg-[#141414]/5 rounded-full transition-colors"
              >
                <XCircle className="w-5 h-5 text-[#141414]/40" />
              </button>
            </div>
            <div className="p-8 flex flex-col items-center gap-8">
              <p className="text-xs font-bold text-[#141414]/60 leading-relaxed text-center">
                Scan this QR code with any mobile device to view this quotation live in the portal.
              </p>
              
              <div className="p-6 bg-white rounded-2xl shadow-inner border border-[#141414]/10 flex items-center justify-center">
                <QRCodeSVG 
                  value={getPortalUrl('quote', selectedQuote.id)} 
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>

              <div className="w-full space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-center opacity-40">Quote Link</p>
                <p className="text-[9px] font-mono text-center font-bold break-all opacity-60">
                  {getPortalUrl('quote', selectedQuote.id)}
                </p>
              </div>

              <button 
                onClick={() => setShowQrModal(false)}
                className="w-full py-4 bg-[#141414] text-[#E4E3E0] font-bold text-xs uppercase tracking-widest rounded-xl transition-all shadow-lg active:scale-95"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Reminders Modal */}
      {isRemindersModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
          <div className="bg-[#E4E3E0] w-full max-w-4xl rounded-3xl border border-[#141414] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-[#141414] flex items-center justify-between bg-primary text-primary-foreground">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6" />
                <h3 className="text-xl font-bold italic serif uppercase tracking-tight">Automated Quote Reminders</h3>
              </div>
              <button 
                onClick={() => setIsRemindersModalOpen(false)} 
                className="p-2 hover:bg-white/10 rounded-full transition-all"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-8 pb-4 space-y-6">
                <div className="flex items-center justify-between p-6 bg-white/40 rounded-3xl border border-primary/10">
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-primary">Enable Automated Reminder System</h4>
                    <p className="text-[10px] font-bold text-primary/40 uppercase tracking-widest">Automatically notify customers via email based on quote expiry</p>
                  </div>
                  <button 
                    onClick={() => setRemindersSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                    className={cn(
                      "w-12 h-6 rounded-full transition-all relative",
                      remindersSettings.enabled ? "bg-green-500 shadow-inner" : "bg-primary/20 shadow-inner"
                    )}
                  >
                    <div className={cn(
                      "w-4 h-4 rounded-full bg-white absolute top-1 transition-all shadow-md",
                      remindersSettings.enabled ? "left-7" : "left-1"
                    )} />
                  </button>
                </div>

              </div>

              {remindersSettings.enabled ? (
                <div className="flex-1 flex overflow-hidden border-t border-black/5 bg-white/30">
                  {/* Left Sidebar - Options */}
                  <div className="w-64 border-r border-black/5 p-6 space-y-3 bg-white/50 overflow-y-auto">
                    {[
                      { id: 'remind7DaysBefore', label: '7 Days Before Expiry' },
                      { id: 'remindOnDueDate', label: 'On Expiry Date' },
                      { id: 'remind7DaysAfter', label: '7 Days After Expiry' },
                      { id: 'remind14DaysAfter', label: '14 Days After Expiry' },
                    ].map((item) => (
                      <div 
                        key={item.id} 
                        className={cn(
                          "flex flex-col gap-2 p-4 rounded-2xl border transition-all cursor-pointer select-none",
                          activeReminderTab === item.id 
                            ? "bg-primary text-primary-foreground border-primary shadow-lg" 
                            : "bg-white border-primary/10 hover:border-primary/40"
                        )}
                        onClick={() => setActiveReminderTab(item.id as any)}
                      >
                         <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-widest">{item.label}</span>
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                setRemindersSettings(prev => ({ ...prev, [item.id]: !prev[item.id as keyof typeof prev] }));
                              }}
                              className={cn(
                                "w-8 h-4 rounded-full transition-all relative border",
                                remindersSettings[item.id as keyof typeof remindersSettings] 
                                  ? (activeReminderTab === item.id ? "bg-white border-white" : "bg-green-500 border-green-600 shadow-inner") 
                                  : "bg-primary/10 border-primary/20 shadow-inner"
                              )}
                            >
                              <div className={cn(
                                "w-2.5 h-2.5 rounded-full absolute top-0.5 transition-all shadow-sm",
                                activeReminderTab === item.id ? "bg-primary" : "bg-white",
                                remindersSettings[item.id as keyof typeof remindersSettings] ? "left-4.5" : "left-1"
                              )} />
                            </div>
                         </div>
                         <div className={cn(
                           "text-[9px] font-bold transition-opacity",
                           activeReminderTab === item.id ? "opacity-70" : "opacity-30"
                         )}>
                           {remindersSettings[item.id as keyof typeof remindersSettings] ? 'Active & Automatic' : 'Inactive'}
                         </div>
                      </div>
                    ))}
                  </div>

                  {/* Template Editor */}
                  <div className="flex-1 p-8 overflow-y-auto custom-scrollbar bg-white">
                    <div className="max-w-2xl mx-auto space-y-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                           <Mail className="w-5 h-5 text-primary" />
                           <h5 className="font-bold text-lg tracking-tight">
                              {activeReminderTab === 'remind7DaysBefore' ? '7 Days Before Template' : 
                               activeReminderTab === 'remindOnDueDate' ? 'Expiry Date Template' :
                               activeReminderTab === 'remind7DaysAfter' ? '7 Days After Template' :
                               '14 Days After Template'}
                           </h5>
                        </div>
                        {remindersSettings[activeReminderTab] ? (
                          <div className="px-3 py-1 bg-green-500 text-white text-[9px] font-black uppercase tracking-widest rounded-full flex items-center gap-1.5 shadow-sm">
                             <Check className="w-3 h-3" />
                             Enabled
                          </div>
                        ) : (
                          <div className="px-3 py-1 bg-slate-100 text-slate-400 text-[9px] font-black uppercase tracking-widest rounded-full border border-slate-200">
                             Disabled
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Email Message Content</label>
                          <textarea 
                            disabled={!remindersSettings[activeReminderTab]}
                            value={remindersSettings.templates[activeReminderTab] || ''}
                            onChange={(e) => setRemindersSettings(prev => ({
                              ...prev,
                              templates: {
                                ...prev.templates,
                                [activeReminderTab]: e.target.value
                              }
                            }))}
                            placeholder="Dear [Customer Name], ..."
                            className={cn(
                              "w-full bg-white border border-primary/20 px-6 py-4 rounded-2xl font-bold text-sm focus:ring-4 focus:ring-primary/10 outline-none min-h-[350px] shadow-sm transition-all custom-scrollbar",
                              !remindersSettings[activeReminderTab] && "opacity-50 cursor-not-allowed bg-slate-50"
                            )}
                          />
                        </div>
                        
                        <div className="bg-[#E4E3E0]/30 p-4 rounded-2xl border border-primary/10 space-y-3">
                           <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary/60 mb-2">
                              <Sparkles className="w-3.5 h-3.5" />
                              Personalization Placeholders
                           </div>
                           <div className="grid grid-cols-2 gap-2">
                              {[
                                { tag: '[Customer Name]', desc: 'Name of the primary contact' },
                                { tag: '[Quote Number]', desc: 'Unique reference number' },
                                { tag: '[Quotation Value]', desc: 'Total amount with currency' },
                                { tag: '[Quote Expiry Date]', desc: 'Quotation expiry date' },
                                { tag: '[Quote Line Items]', desc: 'Detailed table of products/services' },
                                { tag: '[Invoice Status]', desc: 'Information about (partial) invoicing' }
                              ].map(p => (
                                <div key={p.tag} className="flex flex-col bg-white/60 p-2.5 rounded-xl border border-primary/5 hover:border-primary/20 transition-all">
                                   <code className="text-primary font-mono text-[11px] font-bold">{p.tag}</code>
                                   <span className="text-[9px] opacity-40 font-bold uppercase tracking-wider">{p.desc}</span>
                                </div>
                              ))}
                           </div>
                           <p className="text-[9px] italic font-bold text-primary/40 mt-2">These tags will be replaced automatically when the reminder is generated.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-12 text-center space-y-4">
                   <div className="w-20 h-20 bg-primary/5 rounded-full flex items-center justify-center text-primary/20">
                      <Bell className="w-10 h-10" />
                   </div>
                   <div className="space-y-1">
                      <h4 className="font-bold text-lg">Reminder system is currently dormant</h4>
                      <p className="text-xs text-primary/40 max-w-sm">Enable the system above to configure automated customer touchpoints and templates.</p>
                   </div>
                </div>
              )}
            </div>

            <div className="p-8 border-t border-[#141414]/10 bg-white flex justify-end gap-4 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
              <button 
                onClick={() => setIsRemindersModalOpen(false)}
                className="px-8 py-4 border border-primary text-primary rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:bg-primary/5 transition-all text-center"
              >
                Discard Changes
              </button>
              <button 
                onClick={handleSaveReminders}
                className="px-12 py-4 bg-primary text-primary-foreground rounded-2xl font-bold uppercase tracking-widest text-[10px] hover:opacity-90 transition-all shadow-xl active:scale-95 text-center min-w-[200px]"
              >
                Save Activation Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {isPrintModalOpen && docToPrint.length > 0 && (
        <div className="fixed inset-0 bg-primary/20 backdrop-blur-sm z-[250] flex flex-col items-center overflow-auto p-8 print:p-0 print:bg-white print:block">
          <div className="flex flex-col gap-6 w-full max-w-[850px] print:w-full print:max-w-none print:gap-0">
            <div className="flex items-center justify-between bg-white/80 backdrop-blur-lg p-4 rounded-2xl border border-primary/10 shadow-lg print:hidden">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                  <Printer className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-tight">Print Preview</h3>
                  <p className="text-[10px] opacity-50 font-bold uppercase tracking-widest">
                    {docToPrint.length === 1 ? docToPrint[0].number : `${docToPrint.length} quotes selected`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => window.print()}
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-xl font-bold text-xs hover:opacity-90 transition-all shadow-md active:scale-95"
                >
                  <Printer className="w-4 h-4" />
                  Print {docToPrint.length > 1 ? 'All' : 'Now'}
                </button>
                <button 
                  onClick={() => {
                    setIsPrintModalOpen(false);
                    setDocToPrint([]);
                  }}
                  className="p-2.5 hover:bg-red-50 text-red-600 rounded-xl transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {docToPrint.map((quote, index) => (
              <div
                key={quote.id}
                className={cn(
                  "bg-white shadow-2xl rounded-sm overflow-hidden print:shadow-none print:rounded-none",
                  index < docToPrint.length - 1 && "mb-6 print:mb-0 print:break-after-page"
                )}
              >
                <DocumentPrintLayout 
                  organization={organization}
                  document={quote}
                  contact={contacts.find(c => c.id === quote.contactId)}
                  type="quote"
                  language={language as any}
                />
              </div>
            ))}
            
            <div className="flex justify-center pb-12 print:hidden">
               <p className="text-[10px] items-center gap-2 flex font-bold uppercase tracking-widest opacity-40">
                  <span className="w-2 h-2 bg-primary/20 rounded-full animate-pulse" />
                  Standard Odoo-Matching Print Layout
               </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
